import ActionSuccessToast from '../ActionSuccessToast'
import IdeaRemovalToast from './IdeaRemovalToast.jsx'
import { useAssetListRowOrder } from '../../hooks/useAssetListRowOrder.js'
import WatchlistIconStrip from './WatchlistIconStrip.jsx'
import { useWatchlistIconOrder } from '../../hooks/useWatchlistIconOrder.js'
import { useAssetPriceSession } from '../../lib/marketData/assetPriceSessionContext.js'
import { useState, useEffect, useCallback, useMemo, useRef, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  Check,
  CheckSquare,
  ChevronDown,
  Copy,
  Eraser,
  ListPlus,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Trash2,
  Upload,
  X,
} from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { deleteAssetIdeaProjects } from '../../lib/assetIdeas.js'
import { useWatchlistClassifications } from '../../hooks/useWatchlistClassifications.js'
import { useAuth } from '../../hooks/useAuth'
import { useNotifications } from '../../hooks/useNotifications.jsx'
import { subscribeBrowserResume } from '../../lib/marketData/browserResume.js'
import { useBiometricGate } from '../../hooks/useBiometricGate'
import { isOCCSymbol, occIdentityKey, parseOCC } from '../../lib/occParser'
import { resolveSymbolWorkspace } from '../../lib/positionProjects'
import { getMarketSession } from '../../lib/marketSession'
import { useEquityQuoteSessionClock } from '../../hooks/useIbkrQuoteSnapshot.js'
import { useEquityPriceDisplays } from '../../hooks/useEquityPriceDisplays.js'
import {
  ibkrQuoteStream,
  resolveQuoteDisplaySession,
  selectPositionQuotePrice,
} from '../../lib/marketData/ibkrQuoteSubscriptionStore.js'
import { quoteSessionPresentation } from '../../lib/marketData/quoteSessionPresentation.js'
import { ibkrQuoteSnapshotStore } from '../../lib/marketData/ibkrQuoteSnapshotStore.js'
import { ibkrInstrumentQuoteStore } from '../../lib/marketData/ibkrInstrumentQuoteStore.js'
import { derivePositionPnl } from '../../lib/broker/normalizedPnl'
import { computeEquityLotAccounting } from '../../lib/positionLotAccounting'
import { resolvePositionListStage, shouldShowPositionListProject } from '../../lib/positionListStage'
import { collectLiveBrokerOrdersForSymbol } from '../../lib/workspace/resolveWorkspaceState'
import { buildPositionWorkspaceTarget } from '../../lib/positionWorkspaceRoute'
import { useOptionalCommands } from '../../commands/CommandProvider.jsx'
import { SCHWAB_SYNC_COMPLETE_EVENT } from '../../hooks/useAutoSync.js'
import PositionAssetList from './PositionAssetList.jsx'
import OptionChainPicker from './OptionChainPicker.jsx'
import FloatingOptionChainWindow from './FloatingOptionChainWindow.jsx'
import AssetListImportModal from './AssetListImportModal.jsx'
import AssetListImportHistoryModal from './AssetListImportHistoryModal.jsx'
import {
  addVerifiedOptionToPositionAssetList,
  addVerifiedEquityToPositionAssetList,
  archivePositionAssetList,
  assetListViewKey,
  createPositionAssetList,
  defaultPositionAssetListConfiguration,
  normalizePositionAssetListConfiguration,
  positionAssetListColumnsForView,
  readPositionAssetListState,
  readPositionAssetListView,
  removePositionAssetListMembership,
  renamePositionAssetList,
  savePositionAssetListConfiguration,
  searchPositionAssetListInstruments,
} from '../../lib/positionAssetListStore.js'

const POSITION_STAGE_TABS = ['all', 'active', 'working', 'idea', 'closed']
const INSTRUMENT_SEARCH_FILTERS = [
  { id: 'all', label: 'All', ariaLabel: 'All instruments' },
  { id: 'equities', label: 'Stocks & ETFs', ariaLabel: 'Stocks and ETFs' },
  { id: 'options', label: 'Options', ariaLabel: 'Options' },
]
const POSITION_STAGE_LABELS = {
  all: 'All Assets',
  active: 'Active',
  working: 'Working',
  idea: 'Ideas',
  closed: 'Closed',
}
const RAIL_HIGHLIGHT_IDLE_MS = 2500
const ASSET_LIST_COLUMN_LABELS = {
  symbol: 'Symbol',
  last: 'Last',
  changePercent: 'Change %',
  extendedHours: 'Extended hours',
  quantity: 'Quantity',
  pnl: 'P&L',
}

const ASSET_STAGE_PRIORITY = { active: 4, working: 3, idea: 2, planned: 1 }

function assetStageRank(stage) {
  return ASSET_STAGE_PRIORITY[String(stage || '').toLowerCase()] || 0
}

function watchlistNameErrorMessage(error, mode) {
  const message = String(error?.message || '')
  if (/manual_asset_lists_active_name_per_owner|duplicate key value/i.test(message)) {
    return 'A list with this name already exists.'
  }
  return mode === 'rename'
    ? 'Could not rename list. Try again.'
    : 'Could not create list. Try again.'
}

function manualListContainsCandidate(rows, candidate) {
  if (!candidate) return false
  const conid = Number(candidate.conid)
  const symbol = String(candidate.localSymbol || candidate.symbol || '').trim().toUpperCase()
  return rows.some((row) => {
    const rowConid = Number(row?.conid ?? row?.contract?.conid ?? row?.providerMapping?.conid)
    if (Number.isSafeInteger(rowConid) && rowConid > 0) return rowConid === conid
    return String(row?.symbol || row?.displaySymbol || '').trim().toUpperCase() === symbol
  })
}

/* -- Formatters ----------------------------------------------------------- */

// BUG-OMS-LIVE-DAYCHANGE-2026-05-21 Fix 4: single source of truth for day
// change math. Returns nulls (suppresses render) only on missing inputs or
// non-finite results — no percentage cap, since options can legitimately
// move >50% intraday and the prior threshold-based nulling hid real data.
export function computeDayChange(currentPrice, prevClose) {
  if (currentPrice == null || prevClose == null || prevClose <= 0) {
    return { dayChangeDollar: null, dayChangePct: null }
  }
  const dayChangeDollar = currentPrice - prevClose
  const dayChangePct = (dayChangeDollar / prevClose) * 100
  if (!Number.isFinite(dayChangeDollar) || !Number.isFinite(dayChangePct)) {
    return { dayChangeDollar: null, dayChangePct: null }
  }
  return { dayChangeDollar, dayChangePct }
}

function hasUsablePrice(value) {
  const numeric = Number(value)
  return Number.isFinite(numeric) && numeric > 0
}

function usablePrice(value) {
  return hasUsablePrice(value) ? Number(value) : null
}

function hasProviderMarketState(card) {
  return Boolean(
    card?._quoteSession
    || card?._quoteContract
    || hasUsablePrice(card?._last)
    || hasUsablePrice(card?._sessionClose)
    || hasUsablePrice(card?._sessionOpen)
    || hasUsablePrice(card?._lastRth)
    || hasUsablePrice(card?._mark),
  )
}

export function resolvePositionCardSessionPresentation(card, sessionClock, marketSession) {
  const providerSession = card?._quoteContract
    && (card.freshness === 'live' || card._quoteContract.secType === 'STK')
    ? resolveQuoteDisplaySession({
        contract: card._quoteContract,
        session: card._quoteSession || card.session,
      }, sessionClock)
    : null
  return quoteSessionPresentation(providerSession || card?._quoteSession || card?.session || marketSession)
}

export function resolvePositionCardAssetListSession(card, sessionClock, marketSession) {
  const providerSession = card?._quoteContract
    && (card.freshness === 'live' || card._quoteContract.secType === 'STK')
    ? resolveQuoteDisplaySession({
        contract: card._quoteContract,
        session: card._quoteSession || card.session,
      }, sessionClock)
    : null
  return providerSession || card?._quoteSession || card?.session || marketSession || null
}

function positionQuoteRequest(symbol) {
  if (!isOCCSymbol(symbol)) return { streamSymbol: symbol, params: {} }
  const occ = parseOCC(symbol)
  return occ
    ? {
        streamSymbol: occ.underlying,
        params: { secType: 'OPT', expiry: occ.expiry, strike: String(occ.strike), right: occ.right },
      }
    : { streamSymbol: symbol, params: {} }
}

// A provider quote is newer market authority than a broker/account snapshot.
// Workspace selection and shell remounts may refresh account metadata, but
// they must not replace the confirmed quote until a newer provider tick (or an
// explicit quote reset) arrives.
function preserveProviderMarketState(card, previous) {
  if (!previous || !hasProviderMarketState(previous)) return card
  return {
    ...card,
    currentPrice: usablePrice(previous.currentPrice) ?? card.currentPrice,
    prevClose: usablePrice(previous.prevClose) ?? card.prevClose,
    dayChangeDollar: previous.dayChangeDollar ?? card.dayChangeDollar,
    dayChangePct: previous.dayChangePct ?? card.dayChangePct,
    unrealizedPnl: previous.unrealizedPnl ?? card.unrealizedPnl,
    unrealizedPct: previous.unrealizedPct ?? card.unrealizedPct,
    dayPnl: previous.dayPnl ?? card.dayPnl,
    _last: previous._last,
    _sessionClose: previous._sessionClose,
    _sessionOpen: previous._sessionOpen,
    _lastRth: previous._lastRth,
    _mark: previous._mark,
    _bid: previous._bid,
    _ask: previous._ask,
    _quoteSession: previous._quoteSession,
    _quoteContract: previous._quoteContract,
    _brokerCurrentPrice: previous._brokerCurrentPrice,
    _brokerUnrealizedPnl: previous._brokerUnrealizedPnl,
    _brokerUnrealizedPct: previous._brokerUnrealizedPct,
    session: previous.session,
    isExtended: previous.isExtended,
    freshness: previous.freshness,
    freshnessReason: previous.freshnessReason ?? null,
    receivedTs: previous.receivedTs,
  }
}

// Refresh the card metadata without blanking a quote that is already on
// screen. Broker snapshots and live quotes arrive independently, so a fresh
// snapshot can temporarily have no mark even though the existing card does.
function mergeCardsWithStableQuotes(previousCards, nextCards) {
  const previousBySymbol = new Map(
    previousCards.map(card => [String(card.symbol || '').toUpperCase(), card]),
  )

  return nextCards.map((card) => {
    const previous = previousBySymbol.get(String(card.symbol || '').toUpperCase())
    if (hasProviderMarketState(previous)) return preserveProviderMarketState(card, previous)

    const nextPrice = usablePrice(card.currentPrice)
    if (nextPrice != null) {
      return nextPrice === card.currentPrice ? card : { ...card, currentPrice: nextPrice }
    }

    if (!previous || !hasUsablePrice(previous.currentPrice)) return card

    const currentPrice = usablePrice(previous.currentPrice)
    const prevClose = usablePrice(card.prevClose) ?? usablePrice(previous.prevClose)
    const { dayChangeDollar, dayChangePct } = computeDayChange(currentPrice, prevClose)
    const multiplier = card.isOption ? 100 : 1
    const liveMarketValue = card.totalShares > 0
      ? currentPrice * card.totalShares * multiplier
      : null
    const unrealizedPnl = liveMarketValue != null && card.totalCostBasis != null
      ? liveMarketValue - card.totalCostBasis
      : (card.unrealizedPnl ?? previous.unrealizedPnl)
    const unrealizedPct = card.totalCostBasis > 0 && unrealizedPnl != null
      ? (unrealizedPnl / card.totalCostBasis) * 100
      : (card.unrealizedPct ?? previous.unrealizedPct)

    return {
      ...card,
      currentPrice,
      prevClose,
      dayChangeDollar,
      dayChangePct,
      unrealizedPnl,
      unrealizedPct,
      _bid: previous._bid,
      _ask: previous._ask,
    }
  })
}

// Keep the last confirmed rail snapshot outside the workspace component so a
// parent refresh or responsive-shell remount cannot blank cards that were
// already visible. This cache is memory-only and user-scoped: it survives
// React remounts in the current tab, but it is never persisted to browser
// storage and can never bleed between authenticated users.
const positionSwitcherSnapshotByUser = new Map()
const NOOP_REGISTER_COMMAND_HANDLER = () => () => {}

function readPositionSwitcherSnapshot(userId) {
  if (!userId) return []
  return positionSwitcherSnapshotByUser.get(userId) || []
}

function rememberPositionSwitcherSnapshot(userId, cards) {
  if (!userId || !Array.isArray(cards) || cards.length === 0) return cards
  const stableCards = mergeCardsWithStableQuotes(
    readPositionSwitcherSnapshot(userId),
    cards,
  )
  positionSwitcherSnapshotByUser.set(userId, stableCards)
  return stableCards
}

export function resetPositionSwitcherCacheForTests() {
  positionSwitcherSnapshotByUser.clear()
}

function stageTabFor(stage) {
  if (stage === 'planned') return 'idea'
  return stage || 'idea'
}

function ideaCardKey(card) {
  return (card?.projectIds || []).join('|') || `${card?.symbol || 'position'}::${stageTabFor(card?.stage)}`
}

function needsOrderEvidence(project) {
  const stage = String(project?.stage || '').toLowerCase()
  const setupStatus = String(project?.setup_status || '').toLowerCase()
  const tracked = Array.isArray(project?.schwab_order_ids) ? project.schwab_order_ids : []
  return stage === 'working' || setupStatus === 'working' || tracked.length > 0
}

function hasTrackableOrderRef(project) {
  return (Array.isArray(project?.schwab_order_ids) ? project.schwab_order_ids : []).some((tracked) => {
    const accountNumber = String(tracked?.accountNumber ?? tracked?.account_number ?? '').trim()
    const orderId = String(tracked?.orderId ?? tracked?.order_id ?? '').trim()
    return Boolean(accountNumber && orderId)
  })
}

function trackedOrderRefsBySymbol(projects) {
  const refsBySymbol = new Map()
  for (const project of projects || []) {
    const symbolKey = String(project?.symbol || '').toUpperCase()
    if (!symbolKey) continue
    for (const tracked of Array.isArray(project?.schwab_order_ids) ? project.schwab_order_ids : []) {
      const accountNumber = String(tracked?.accountNumber ?? tracked?.account_number ?? '').trim()
      const orderId = String(tracked?.orderId ?? tracked?.order_id ?? '').trim()
      if (!accountNumber || !orderId) continue
      const refs = refsBySymbol.get(symbolKey) || new Set()
      refs.add(`${accountNumber}:${orderId}`)
      refsBySymbol.set(symbolKey, refs)
    }
  }
  return refsBySymbol
}

async function fetchTrackedOrderEvidence(projects) {
  const refsBySymbol = trackedOrderRefsBySymbol(projects)
  const orderRefs = [...new Set([...refsBySymbol.values()].flatMap(refs => [...refs]))].sort()
  if (orderRefs.length === 0) return null

  try {
    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token
    if (!token) return null

    const params = new URLSearchParams({ orderRefs: orderRefs.join(',') })
    const res = await fetch(`/api/schwab/orders?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) return null

    const data = await res.json()
    const allOrders = Array.isArray(data?.orders) ? data.orders : []
    const evidence = new Map()
    for (const symbolKey of refsBySymbol.keys()) {
      evidence.set(
        symbolKey,
        collectLiveBrokerOrdersForSymbol(allOrders, symbolKey).length > 0,
      )
    }
    return evidence
  } catch {
    return null
  }
}

function mergeCardsPreservingMarketState(previousCards, nextCards) {
  return nextCards.map((card) => {
    const nextProjectIds = new Set(card.projectIds || [])
    const previous = previousCards.find(previousCard =>
      (previousCard.projectIds || []).some(projectId => nextProjectIds.has(projectId))
    )
    if (!previous) return card

    if (hasProviderMarketState(previous)) {
      return preserveProviderMarketState(card, previous)
    }

    return {
      ...card,
      currentPrice: usablePrice(previous.currentPrice) ?? card.currentPrice,
      prevClose: usablePrice(previous.prevClose) ?? card.prevClose,
      dayChangeDollar: previous.dayChangeDollar ?? card.dayChangeDollar,
      dayChangePct: previous.dayChangePct ?? card.dayChangePct,
      unrealizedPnl: previous.unrealizedPnl ?? card.unrealizedPnl,
      unrealizedPct: previous.unrealizedPct ?? card.unrealizedPct,
      dayPnl: previous.dayPnl ?? card.dayPnl,
      _bid: previous._bid,
      _ask: previous._ask,
    }
  })
}

export function applyAuthoritativeQuoteSnapshot(card, snapshot, marketSession) {
  if (!card || !snapshot) return card
  const isOpt = Boolean(card.isOption)
  const currentPrice = selectPositionQuotePrice(
    snapshot,
    snapshot.session || marketSession,
    isOpt ? 'OPT' : (snapshot.contract?.secType || 'STK'),
  )
  if (!(currentPrice > 0)) {
    if (snapshot.freshness !== 'unavailable') return { ...card, freshness: snapshot.freshness || card.freshness, freshnessReason: null }
    return {
      ...card,
      currentPrice: null,
      prevClose: null,
      dayChangeDollar: null,
      dayChangePct: null,
      unrealizedPnl: null,
      unrealizedPct: null,
      dayPnl: null,
      _last: null,
      _sessionClose: null,
      _sessionOpen: null,
      _lastRth: null,
      _mark: null,
      _bid: null,
      _ask: null,
      _quoteSession: snapshot.session || card._quoteSession,
      _quoteContract: snapshot.contract || card._quoteContract,
      freshness: 'unavailable',
      freshnessReason: null,
      receivedTs: snapshot.receivedTs || snapshot.ts || card.receivedTs || null,
    }
  }
  // `lastRth` is the latest regular-hours trade, not the previous close. Using
  // it as the comparison baseline during RTH fabricates a +0.00% move.
  const prevClose = usablePrice(snapshot.close) ?? usablePrice(card.prevClose)
  const { dayChangeDollar, dayChangePct } = computeDayChange(currentPrice, prevClose)
  const multiplier = isOpt ? 100 : 1
  const liveMktVal = card.totalShares > 0 ? currentPrice * card.totalShares * multiplier : null
  const unrealizedPnl = liveMktVal != null && card.totalCostBasis != null
    ? liveMktVal - card.totalCostBasis
    : card.unrealizedPnl
  const unrealizedPct = card.totalCostBasis > 0 && unrealizedPnl != null
    ? (unrealizedPnl / card.totalCostBasis) * 100
    : card.unrealizedPct
  return {
    ...card,
    currentPrice,
    prevClose,
    dayChangeDollar,
    dayChangePct,
    unrealizedPnl,
    unrealizedPct,
    session: snapshot.session || card.session,
    isExtended: quoteSessionPresentation(snapshot.session || card.session) != null,
    _last: usablePrice(snapshot.last),
    _sessionClose: usablePrice(snapshot.sessionClose),
    _sessionOpen: usablePrice(snapshot.open),
    _lastRth: usablePrice(snapshot.lastRth),
    _mark: usablePrice(snapshot.mark),
    _bid: usablePrice(snapshot.bid),
    _ask: usablePrice(snapshot.ask),
    _quoteSession: snapshot.session || card._quoteSession,
    _quoteContract: snapshot.contract || card._quoteContract,
    freshness: snapshot.freshness || card.freshness || null,
    freshnessReason: snapshot.freshnessReason ?? null,
    receivedTs: snapshot.receivedTs || snapshot.ts || card.receivedTs || null,
  }
}

function cardCreatedAtSortValue(card) {
  const value = Date.parse(card?.created_at)
  if (Number.isFinite(value)) return value
  // A live broker-only row with no snapshot timestamp should remain easy to
  // discover without fabricating a holding age. Newest puts it first; oldest
  // naturally puts it last.
  return card?.brokerSnapshotOnly ? Number.MAX_SAFE_INTEGER : 0
}

/* -- Component ------------------------------------------------------------ */

export default function PositionSwitcher({
  currentSymbol,
  onClose,
  onOpenPosition,
  onOpenSymbol,
  onOpenWatchlist,
  symbolSearchRequested = false,
  onSymbolSearchRequestHandled,
  refreshKey = 0,
  confirmedRestoredIdea = null,
  onRestoredIdeaPublished,
  workingOrdersApi = null,
}) {
  const { user } = useAuth()
  const { notifications } = useNotifications()
  const brokerNotificationIdsRef = useRef({ userId: null, ids: new Set() })
  const registerCommandHandler = useOptionalCommands()?.registerCommandHandler || NOOP_REGISTER_COMMAND_HANDLER
  const biometricGate = useBiometricGate()
  const navigate = useNavigate()
  const { projectId: currentProjectId } = useParams()
  const [searchParams] = useSearchParams()
  const [positions, setPositions] = useState(() => readPositionSwitcherSnapshot(user?.id))
  const [loading, setLoading] = useState(() => readPositionSwitcherSnapshot(user?.id).length === 0)
  const [positionLoadError, setPositionLoadError] = useState(null)
  const [activeStageTab, setActiveStageTab] = useState('all')
  const [ideaSelectMode, setIdeaSelectMode] = useState(false)
  const [selectedIdeaKeys, setSelectedIdeaKeys] = useState(() => new Set())
  const [ideaDeletePending, setIdeaDeletePending] = useState(false)
  const [ideaDeleteNotice, setIdeaDeleteNotice] = useState(null)
  const [pendingIdeaIds, setPendingIdeaIds] = useState(() => new Set())
  const ideaRemovalRef = useRef(null)
  const ideaNoticeTimerRef = useRef(null)
  const ideaOwnerRef = useRef(user?.id)
  ideaOwnerRef.current = user?.id
  useEffect(() => {
    setPendingIdeaIds(new Set())
    setIdeaDeleteNotice(null)
    setIdeaDeletePending(false)
    return () => {
      // Leaving the workspace or signing out cancels an uncommitted removal.
      const operation = ideaRemovalRef.current
      if (operation) { operation.cancelled = true; operation.cancel?.() }
      ideaRemovalRef.current = null
      clearTimeout(ideaNoticeTimerRef.current)
    }
  }, [user?.id])
  const [sortMode, setSortMode] = useState('newest')
  const assetListSort = { field: 'manual', direction: 'asc' }
  const [lifecycleSections, setLifecycleSections] = useState({
    active: true,
    working: true,
    ideas: true,
    recentlyClosed: true,
  })
  const [showSortMenu, setShowSortMenu] = useState(false)
  const [showListMenu, setShowListMenu] = useState(false)
  const [showSymbolSearch, setShowSymbolSearch] = useState(false)
  const [symbolSearchMode, setSymbolSearchMode] = useState('navigate')
  const [durableAssetLists, setDurableAssetLists] = useState([])
  const [localAssetListRows, setLocalAssetListRows] = useState([])
  const [durableAssetListLoading, setDurableAssetListLoading] = useState(true)
  const [activeManualListId, setActiveManualListId] = useState(null)
  const priceSession = useAssetPriceSession()
  const sessionViewKey = activeStageTab === 'watchlists' && activeManualListId
    ? `manual-list:${activeManualListId}` : activeStageTab === 'all' ? 'all-assets'
    : `lifecycle:${activeStageTab === 'idea' ? 'ideas' : activeStageTab === 'closed' ? 'recently-closed' : activeStageTab}`
  const warmRows = priceSession?.views[sessionViewKey]
  const durableAssetListRows = warmRows ?? localAssetListRows
  const setDurableAssetListRows = value => {
    const next = typeof value === 'function' ? value(durableAssetListRows) : value
    setLocalAssetListRows(next)
    priceSession?.updateView(sessionViewKey, next)
  }
  const [watchlistEditorMode, setWatchlistEditorMode] = useState(null)
  const [showCsvImport, setShowCsvImport] = useState(false)
  const [historyList, setHistoryList] = useState(null)
  const importOwnerRef = useRef(user?.id)
  importOwnerRef.current = user?.id
  const [watchlistNameDraft, setWatchlistNameDraft] = useState('')
  const [watchlistMutationPending, setWatchlistMutationPending] = useState(false)
  const orderLabel = activeStageTab === 'watchlists'
    ? durableAssetLists.find(list => list.listId === activeManualListId)?.name || 'Watchlist'
    : POSITION_STAGE_LABELS[activeStageTab] || 'Asset List'
  const ideaOrder = useAssetListRowOrder(user?.id, 'lifecycle:ideas', 'Ideas')
  const otherListOrder = useAssetListRowOrder(sessionViewKey === 'lifecycle:ideas' ? null : user?.id, sessionViewKey, orderLabel)
  const listOrder = sessionViewKey === 'lifecycle:ideas' ? ideaOrder : otherListOrder
  const watchlistIconOrder = useWatchlistIconOrder(user?.id, durableAssetLists)
  const [assetListConfigurations, setAssetListConfigurations] = useState({})
  const [assetListSettingsOwner, setAssetListSettingsOwner] = useState(null)
  const [configurationSaving, setConfigurationSaving] = useState(false)
  const configurationSaveRef = useRef(null)
  const [assetListPersistenceError, setAssetListPersistenceError] = useState(null)
  const [assetListNotice, setAssetListNotice] = useState(null)
  const sortMenuRef = useRef(null)
  const listMenuRef = useRef(null)

  const closeWatchlistEditor = useCallback(() => {
    setWatchlistEditorMode(null)
    setWatchlistNameDraft('')
    setAssetListPersistenceError(null)
  }, [])
  // The portal can mount after an async Asset List load or after returning
  // from the option chain. Focus at attachment, before the next keystroke.
  const focusSymbolSearchInput = useCallback(node => {
    node?.focus({ preventScroll: true })
  }, [])
  const loadRequestRef = useRef(0)
  const loadedUserIdRef = useRef(positions.length > 0 ? user?.id : null)
  const currentSymbolRef = useRef(currentSymbol)
  const workingOrdersApiRef = useRef(workingOrdersApi)
  const sessionClock = useEquityQuoteSessionClock()
  const quoteSnapshotVersion = useSyncExternalStore(
    ibkrQuoteSnapshotStore.subscribe,
    ibkrQuoteSnapshotStore.getVersion,
    ibkrQuoteSnapshotStore.getVersion,
  )
  const instrumentQuoteVersion = useSyncExternalStore(
    ibkrInstrumentQuoteStore.subscribe,
    ibkrInstrumentQuoteStore.getVersion,
    ibkrInstrumentQuoteStore.getVersion,
  )
  const marketSession = useMemo(() => getMarketSession(new Date(sessionClock)), [sessionClock])
  const [openSymbol, setOpenSymbol] = useState('')
  const [instrumentSearch, setInstrumentSearch] = useState({ status: 'idle', results: [] })
  const [selectedInstrumentIndex, setSelectedInstrumentIndex] = useState(0)
  const [instrumentSearchFilter, setInstrumentSearchFilter] = useState('all')
  const [opening, setOpening] = useState(false)
  const [openError, setOpenError] = useState(null)
  const [optionChainUnderlying, setOptionChainUnderlying] = useState(null)
  const [addingOptionConid, setAddingOptionConid] = useState(null)
  const instrumentSearchRequestRef = useRef(0)
  const [highlightedCardKey, setHighlightedCardKey] = useState(null)
  const visibleAssetCardsRef = useRef([])
  const handleVisibleAssetCardsChange = useCallback(cards => {
    visibleAssetCardsRef.current = cards
  }, [])
  const cardRefs = useRef(new Map())

  const visibleInstrumentResults = useMemo(() => {
    if (instrumentSearchFilter === 'options') {
      return instrumentSearch.results.filter(candidate => candidate.derivativeSecTypes?.includes('OPT'))
    }
    return instrumentSearch.results
  }, [instrumentSearch.results, instrumentSearchFilter])

  useEffect(() => {
    setSelectedInstrumentIndex(index => Math.min(index, Math.max(0, visibleInstrumentResults.length - 1)))
  }, [visibleInstrumentResults.length])

  const activeOptionIdentityKeys = useMemo(() => new Set(
    positions
      .filter(card => card.stage === 'active' && Math.abs(Number(card.totalShares) || 0) > 0)
      .map(card => occIdentityKey(card.symbol))
      .filter(Boolean),
  ), [positions])
  currentSymbolRef.current = currentSymbol
  workingOrdersApiRef.current = workingOrdersApi

  useEffect(() => {
    let cancelled = false
    setDurableAssetLists([])
    setAssetListConfigurations({})
    setAssetListSettingsOwner(null)
    configurationSaveRef.current = null
    setConfigurationSaving(false)
    setAssetListPersistenceError(null)
    if (!user?.id || typeof supabase.rpc !== 'function') return () => { cancelled = true }
    readPositionAssetListState()
      .then((state) => {
        if (cancelled) return
        setAssetListSettingsOwner(user.id)
        setDurableAssetLists(state.lists)
        setActiveManualListId(current => (
          state.lists.some(list => list.listId === current) ? current : (state.lists[0]?.listId || null)
        ))
        setAssetListConfigurations(Object.fromEntries(state.configurations.map(record => [
          assetListViewKey(record.viewRef),
          {
            version: Number(record.configVersion) || 0,
            configuration: normalizePositionAssetListConfiguration(record.viewRef, record.configuration),
          },
        ])))
      })
      .catch(() => {
        if (!cancelled) setAssetListPersistenceError('Could not load saved Asset List settings.')
      })
    return () => { cancelled = true }
  }, [user?.id])

  useEffect(() => {
    if (!user?.id || positions.length === 0) return
    rememberPositionSwitcherSnapshot(user.id, positions)
  }, [positions, user?.id])

  const addCandidateToActiveManualList = useCallback(async (candidate, { closeSearch = false, showRailNotice = false } = {}) => {
    if (!user || opening || watchlistMutationPending) return false
    const normalized = String(candidate?.symbol || '').trim().toUpperCase()
    const list = durableAssetLists.find(item => item.listId === activeManualListId)
    if (!list) {
      const message = 'Select a custom watchlist first'
      if (showRailNotice) setAssetListNotice({ tone: 'error', text: message })
      else setOpenError(message)
      return false
    }
    if (manualListContainsCandidate(durableAssetListRows, candidate)) {
      if (showRailNotice) setAssetListNotice({ tone: 'success', text: `${normalized} is already in ${list.name}` })
      return true
    }

    setWatchlistMutationPending(true)
    setOpenError(null)
    if (showRailNotice) setAssetListNotice(null)
    try {
      const reconcileList = async () => {
        const state = await readPositionAssetListState()
        const reconciledList = state.lists.find(item => item.listId === list.listId)
        if (!reconciledList) throw new Error(`${list.name} no longer exists`)
        setDurableAssetLists(state.lists)
        const rows = await readPositionAssetListView({ kind: 'manual-list', listId: list.listId })
        setDurableAssetListRows(rows)
        return { list: reconciledList, rows }
      }

      let reconciled = await reconcileList()
      if (!manualListContainsCandidate(reconciled.rows, candidate)) {
        let result
        try {
          result = await addVerifiedEquityToPositionAssetList(
            list.listId,
            candidate,
            Number(reconciled.list.version),
          )
        } catch (error) {
          if (error?.code !== 'ASSET_LIST_VERSION_CONFLICT') throw error
          reconciled = await reconcileList()
          if (!manualListContainsCandidate(reconciled.rows, candidate)) {
            result = await addVerifiedEquityToPositionAssetList(
              list.listId,
              candidate,
              Number(reconciled.list.version),
            )
          }
        }
        if (result) {
          setDurableAssetLists(current => current.map(item => (
            item.listId === list.listId
              ? { ...item, version: Number(result.listVersion) }
              : item
          )))
        }
      }

      const rows = await readPositionAssetListView({ kind: 'manual-list', listId: list.listId })
      setDurableAssetListRows(rows)
      if (showRailNotice) setAssetListNotice({ tone: 'success', text: `${normalized} added to ${list.name}` })
      if (closeSearch) {
        setOpenSymbol('')
        setShowSymbolSearch(false)
      }
      return true
    } catch (error) {
      const message = error?.message || `Could not add ${normalized}`
      if (showRailNotice) setAssetListNotice({ tone: 'error', text: message })
      else setOpenError(message)
      return false
    } finally {
      setWatchlistMutationPending(false)
    }
  }, [activeManualListId, durableAssetListRows, durableAssetLists, opening, user, watchlistMutationPending])

  const handleOpenSymbol = async (e, candidateOverride = null) => {
    e.preventDefault()
    if (!user || opening || watchlistMutationPending) return
    const candidate = candidateOverride || visibleInstrumentResults[selectedInstrumentIndex] || null
    const normalized = String(candidate?.symbol || '').trim().toUpperCase()
    if (!candidate) {
      setOpenError(openSymbol.trim() ? 'Select a verified IBKR result' : 'Symbol is required')
      return
    }
    if (symbolSearchMode === 'add' && activeStageTab === 'watchlists') {
      await addCandidateToActiveManualList(candidate, { closeSearch: true })
      return
    }
    if (typeof onOpenSymbol === 'function') {
      if (!normalized) {
        setOpenError('Symbol is required')
        return
      }
      onOpenSymbol(normalized)
      setOpenSymbol('')
      setOpenError(null)
      setShowSymbolSearch(false)
      if (onClose) onClose()
      return
    }
    setOpening(true)
    setOpenError(null)
    try {
      const result = await resolveSymbolWorkspace(user.id, normalized)
      setOpenSymbol('')
      setShowSymbolSearch(false)
      navigate(result.path)
      if (onClose) onClose()
    } catch (err) {
      setOpenError(err.message)
    } finally {
      setOpening(false)
    }
  }

  const handleOpenOptionContract = async (contract) => {
    const occSymbol = String(contract?.localSymbol || '').trim().toUpperCase()
    if (!isOCCSymbol(occSymbol)) {
      setOpenError('IBKR returned an invalid OCC option identity')
      return
    }
    if (typeof onOpenSymbol === 'function') {
      onOpenSymbol(occSymbol)
      setShowSymbolSearch(false)
      if (onClose) onClose()
      return
    }
    setOpening(true)
    setOpenError(null)
    try {
      const result = await resolveSymbolWorkspace(user.id, occSymbol)
      setShowSymbolSearch(false)
      navigate(result.path)
      if (onClose) onClose()
    } catch (error) {
      setOpenError(error?.message || 'Could not open option chart')
    } finally {
      setOpening(false)
    }
  }

  const handleInspectOptionContractWatchlists = async (contract) => {
    try {
      const memberships = await Promise.all(durableAssetLists.map(async (list) => ({
        listId: list.listId,
        rows: await readPositionAssetListView({ kind: 'manual-list', listId: list.listId }),
      })))
      return memberships
        .filter(({ rows }) => manualListContainsCandidate(rows, contract))
        .map(({ listId }) => listId)
    } catch (error) {
      setOpenError(error?.message || 'Could not check watchlist memberships')
      throw error
    }
  }

  const handleAddOptionContract = async (contract, targetListId = activeManualListId) => {
    const list = durableAssetLists.find(candidate => candidate.listId === targetListId)
    if (!list || !optionChainUnderlying) throw new Error('Choose a watchlist before adding this option contract')
    if (addingOptionConid) throw new Error('Another option contract is already being added')
    setAddingOptionConid(contract.conid)
    setOpenError(null)
    try {
      const reconcileList = async () => {
        const state = await readPositionAssetListState()
        const currentList = state.lists.find(candidate => candidate.listId === list.listId)
        if (!currentList) throw new Error(`${list.name} no longer exists`)
        setDurableAssetLists(state.lists)
        const rows = await readPositionAssetListView({ kind: 'manual-list', listId: list.listId })
        if (list.listId === activeManualListId) setDurableAssetListRows(rows)
        return { list: currentList, rows }
      }

      let reconciled = await reconcileList()
      if (manualListContainsCandidate(reconciled.rows, contract)) return

      let result
      try {
        result = await addVerifiedOptionToPositionAssetList(
          list.listId, contract, optionChainUnderlying, Number(reconciled.list.version),
        )
      } catch (error) {
        if (error?.code !== 'ASSET_LIST_VERSION_CONFLICT') throw error
        reconciled = await reconcileList()
        if (manualListContainsCandidate(reconciled.rows, contract)) return
        result = await addVerifiedOptionToPositionAssetList(
          list.listId, contract, optionChainUnderlying, Number(reconciled.list.version),
        )
      }
      setDurableAssetLists(current => current.map(candidate => (
        candidate.listId === list.listId
          ? { ...candidate, version: Number(result.listVersion) }
          : candidate
      )))
      if (list.listId === activeManualListId) {
        setDurableAssetListRows(await readPositionAssetListView({ kind: 'manual-list', listId: list.listId }))
      }
    } catch (error) {
      setOpenError(error?.message || 'Could not add option contract')
      throw error
    } finally {
      setAddingOptionConid(null)
    }
  }

  // Close sort menu on click outside
  useEffect(() => {
    if (!showSortMenu) return
    const handler = (e) => {
      if (sortMenuRef.current && !sortMenuRef.current.contains(e.target)) {
        setShowSortMenu(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showSortMenu])

  useEffect(() => {
    if (!showListMenu) return
    const handler = (event) => {
      if (listMenuRef.current && !listMenuRef.current.contains(event.target)) {
        setShowListMenu(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showListMenu])

  useEffect(() => {
    if (!symbolSearchRequested) return
    setOpenSymbol('')
    setOpenError(null)
    setSymbolSearchMode('navigate')
    setInstrumentSearchFilter('all')
    setShowSymbolSearch(true)
    onSymbolSearchRequestHandled?.()
  }, [onSymbolSearchRequestHandled, symbolSearchRequested])

  useEffect(() => {
    if (!showSymbolSearch) {
      setInstrumentSearch({ status: 'idle', results: [] })
      setSelectedInstrumentIndex(0)
      setOptionChainUnderlying(null)
      setAddingOptionConid(null)
      return undefined
    }
    const query = String(openSymbol || '').trim().toUpperCase().replace(/\s+/g, ' ')
    const requestId = instrumentSearchRequestRef.current + 1
    instrumentSearchRequestRef.current = requestId
    setSelectedInstrumentIndex(0)
    setOpenError(null)
    if (query.length < 2) {
      setInstrumentSearch({ status: 'idle', results: [] })
      return undefined
    }
    const controller = new AbortController()
    setInstrumentSearch({ status: 'searching', results: [] })
    const timer = window.setTimeout(() => {
      searchPositionAssetListInstruments(query, { signal: controller.signal })
        .then((response) => {
          if (controller.signal.aborted || instrumentSearchRequestRef.current !== requestId) return
          setInstrumentSearch({ status: 'ready', results: response.results || [] })
        })
        .catch((error) => {
          if (controller.signal.aborted || instrumentSearchRequestRef.current !== requestId) return
          setInstrumentSearch({
            status: error?.code === 'PROVIDER_UNAVAILABLE' ? 'unavailable' : 'error',
            results: [],
          })
        })
    }, 250)
    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [openSymbol, showSymbolSearch])

  useEffect(() => {
    if (!showSymbolSearch) return undefined
    const closeOnEscape = (event) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopPropagation()
      setShowSymbolSearch(false)
    }
    document.addEventListener('keydown', closeOnEscape, true)
    return () => document.removeEventListener('keydown', closeOnEscape, true)
  }, [showSymbolSearch])


  const sortOptions = [
    { key: 'newest', label: 'Newest First' },
    { key: 'oldest', label: 'Oldest First' },
    { key: 'pnl_desc', label: 'P&L High → Low' },
    { key: 'pnl_asc', label: 'P&L Low → High' },
    { key: 'alpha', label: 'Alphabetical' },
  ]

  // Current project IDs (to highlight active card)
  const currentIds = useMemo(() => {
    const idsParam = searchParams.get('ids')
    const projectIdParam = searchParams.get('projectId')
    if (idsParam) return new Set(idsParam.split(',').filter(Boolean))
    if (projectIdParam) return new Set([projectIdParam])
    return currentProjectId ? new Set([currentProjectId]) : new Set()
  }, [searchParams, currentProjectId])

  const toggleIdeaCardSelection = useCallback((card) => {
    const key = ideaCardKey(card)
    setSelectedIdeaKeys(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const loadPositions = useCallback(async () => {
    if (!user) {
      loadRequestRef.current += 1
      if (loadedUserIdRef.current) {
        positionSwitcherSnapshotByUser.delete(loadedUserIdRef.current)
      }
      loadedUserIdRef.current = null
      setPositions([])
      setPositionLoadError(null)
      setLoading(true)
      return
    }

    const requestId = ++loadRequestRef.current
    const isLatestRequest = () => loadRequestRef.current === requestId
    if (loadedUserIdRef.current !== user.id) {
      const cachedCards = readPositionSwitcherSnapshot(user.id)
      setPositions(cachedCards)
      setLoading(cachedCards.length === 0)
    }

    // Get all active + planned position projects
    const fetchProjects = () => supabase
      .from('position_projects')
      .select('id, symbol, stage, trade_id, created_at, setup_status, trigger_price, pattern_failure_price, sell_target_price, setup_source, account_sizing, setup_pattern, direction, recommended_size_pct, schwab_order_ids')
      .eq('user_id', user.id)
      .in('stage', ['active', 'working', 'idea', 'planned'])
      .order('created_at', { ascending: false })

    const fetchBrokerPositions = () => supabase
      .from('schwab_positions')
      .select('account_number, symbol, instrument_type, quantity, market_value, current_day_pnl, average_price, prev_close_price, status, raw, cost_basis_long, cost_basis_short, unrealized_pnl_long, unrealized_pnl_short, cost_basis_source, currency, contract_multiplier, last_updated_at')
      .eq('user_id', user.id)
      .or('status.eq.open,status.is.null')

    const [projectResult, brokerPositionResult] = await Promise.all([
      fetchProjects(),
      fetchBrokerPositions(),
    ])
    let { data: projects, error: projectsError } = projectResult

    if (!isLatestRequest()) return

    // A single empty background read must not erase an established trading
    // rail. Confirm it once before accepting the authoritative empty state.
    // Legitimate all-positions-closed state still clears immediately after
    // the second successful read.
    if (!projectsError && (!projects || projects.length === 0) && readPositionSwitcherSnapshot(user.id).length > 0) {
      const retry = await fetchProjects()
      if (!isLatestRequest()) return
      projects = retry.data
      projectsError = retry.error
    }

    const brokerRows = brokerPositionResult.data || []
    const cachedCards = readPositionSwitcherSnapshot(user.id)
    const projectsUnavailable = Boolean(projectsError)
    const brokerPositionsUnavailable = Boolean(brokerPositionResult.error)

    // An incomplete read cannot replace a confirmed rail: a failed holdings
    // read must not demote a broker-confirmed Active asset back to Working.
    // Keep the snapshot until both lifecycle sources can be read again.
    if ((projectsUnavailable || brokerPositionsUnavailable) && cachedCards.length > 0) {
      setPositionLoadError(null)
      setLoading(false)
      return
    }

    // Neither source may authoritatively clear the other. If the only source
    // that could supply cards failed, preserve the last confirmed snapshot;
    // on a cold load, show an explicit retry state instead of a false empty.
    if (
      (projectsUnavailable && brokerRows.length === 0)
      || (brokerPositionsUnavailable && (!projects || projects.length === 0))
    ) {
      setPositionLoadError(cachedCards.length > 0 ? null : 'Could not load positions. Try again.')
      setLoading(false)
      return
    }

    setPositionLoadError(null)
    if (projectsUnavailable) projects = []
    if ((!projects || projects.length === 0) && brokerRows.length === 0) {
      loadedUserIdRef.current = user.id
      positionSwitcherSnapshotByUser.delete(user.id)
      setPositions([])
      setLoading(false)
      return
    }

    projects = projects || []

    // A broker position is authoritative even when the workspace project has
    // not been created yet. Synthesize a read-only active rail source so the
    // position remains visible; opening it routes through the normal symbol
    // workspace and does not place or modify broker orders.
    const managedPositionSymbols = new Set(
      projects
        .filter(project => ['active', 'working'].includes(String(project.stage || '').toLowerCase()))
        .map(project => String(project.symbol || '').toUpperCase()),
    )
    const brokerOnlyBySymbol = new Map()
    for (const row of brokerRows) {
      if (row.status === 'closed') continue
      const symbol = String(row.symbol || '').toUpperCase()
      const quantity = Number(row.quantity) || 0
      if (!symbol || quantity === 0 || managedPositionSymbols.has(symbol)) continue
      const existing = brokerOnlyBySymbol.get(symbol) || {
        longQuantity: 0,
        shortQuantity: 0,
        hasOpenLot: false,
        lastUpdatedAt: null,
      }
      if (quantity > 0) existing.longQuantity += quantity
      if (quantity < 0) existing.shortQuantity += Math.abs(quantity)
      existing.hasOpenLot = true
      if (row.last_updated_at && (!existing.lastUpdatedAt || row.last_updated_at > existing.lastUpdatedAt)) {
        existing.lastUpdatedAt = row.last_updated_at
      }
      brokerOnlyBySymbol.set(symbol, existing)
    }
    for (const [symbol, snapshot] of brokerOnlyBySymbol) {
      if (!snapshot.hasOpenLot) continue
      projects.push({
        id: null,
        symbol,
        stage: 'active',
        trade_id: null,
        created_at: snapshot.lastUpdatedAt,
        setup_status: null,
        schwab_order_ids: [],
        direction: snapshot.shortQuantity > snapshot.longQuantity ? 'SHORT' : 'LONG',
        __brokerSnapshotOnly: true,
      })
    }

    // Trades depend on projects; the broker snapshot was fetched independently
    // so an absent project can never hide a real open position.
    const tradeIds = projects.map(p => p.trade_id).filter(Boolean)
    const symbols = [...new Set(projects.map(p => p.symbol))]

    const [tradeResult, schwabResult] = await Promise.all([
      tradeIds.length > 0
        ? supabase.from('trades').select('id, symbol, net_pnl, pnl_percent, status, stop_loss, quantity_open').in('id', tradeIds)
        : Promise.resolve({ data: [] }),
      Promise.resolve({ data: brokerRows }),
    ])

    const tradeMap = Object.fromEntries((tradeResult.data || []).map(t => [t.id, t]))
    const heldSymbols = new Set(brokerRows
      .filter(row => row.status !== 'closed' && Math.abs(Number(row.quantity) || 0) > 0)
      .map(row => String(row.symbol || '').toUpperCase()))
    const activeSymbols = [...new Set(projects
      .filter(p => resolvePositionListStage(p, {
        hasLivePosition: heldSymbols.has(String(p.symbol || '').toUpperCase()),
      }) === 'active')
      .map(p => String(p.symbol || '').toUpperCase())
      .filter(Boolean))]
    let executionMap = {}
    if (activeSymbols.length > 0) {
      const { data: activeTrades } = await supabase
        .from('trades')
        .select('id, symbol, status')
        .eq('user_id', user.id)

      const activeTradeRows = (activeTrades || []).filter(t =>
        activeSymbols.includes(String(t.symbol || '').toUpperCase())
        && (t.status === 'open' || t.status === 'partial')
      )
      const activeTradeIds = activeTradeRows.map(t => t.id).filter(Boolean)

      if (activeTradeIds.length > 0) {
        const { data: executions } = await supabase
          .from('executions')
          .select('trade_id, account_number, symbol, execution_type, side, quantity, price, commission, fees, executed_at')
          .in('trade_id', activeTradeIds)

        for (const exec of executions || []) {
          const sym = String(exec.symbol || '').toUpperCase()
          if (!sym) continue
          if (!executionMap[sym]) executionMap[sym] = []
          executionMap[sym].push(exec)
        }
      }
    }
    let posMap = {}

    // Aggregate schwab positions by symbol
    for (const sp of (schwabResult.data || [])) {
      if (sp.status === 'closed') continue
      const sym = String(sp.symbol || '').toUpperCase()
      if (!sym) continue
      if (!posMap[sym]) {
        // Detect inverse ETFs via description (e.g. "ProShares UltraShort S&P500")
        let isInverse = false
        if (sp.instrument_type !== 'OPTION') {
          const desc = (sp.raw?.instrument?.description || '').toLowerCase()
          if (desc && !desc.includes('bull')) {
            isInverse = /\b(inverse|short|ultrashort|ultra short|bear)\b/.test(desc)
          }
        }
        posMap[sym] = { totalMktVal: 0, totalCostBasis: 0, totalAvgEntryValue: 0, totalDayPnl: 0, totalQty: 0, prevCloseVal: 0, currentPrice: 0, fromCache: false, isOption: sp.instrument_type === 'OPTION', isInverse, accountNumbers: new Set() }
      }
      const qty = Number(sp.quantity) || 0
      const avg = Number(sp.average_price) || 0
      const optMul = sp.instrument_type === 'OPTION' ? 100 : 1
      const prevClose = Number(sp.prev_close_price) || 0
      const { costBasis } = derivePositionPnl(sp)
      posMap[sym].totalMktVal += Number(sp.market_value) || 0
      posMap[sym].totalCostBasis += costBasis
      posMap[sym].totalAvgEntryValue += avg * Math.abs(qty) * optMul
      posMap[sym].totalDayPnl += prevClose > 0
        ? (Number(sp.market_value) || 0) - prevClose * qty * optMul
        : (Number(sp.current_day_pnl) || 0)
      posMap[sym].totalQty += qty
      posMap[sym].prevCloseVal += prevClose * qty * optMul
      if (sp.account_number) posMap[sym].accountNumbers.add(String(sp.account_number))
    }

    // First-pass prices from schwab only — IBKR quotes patch in later
    for (const sym of Object.keys(posMap)) {
      const d = posMap[sym]
      const optDiv = d.isOption ? 100 : 1
      const schwabPrice = !d.fromCache && d.totalQty > 0 ? d.totalMktVal / (d.totalQty * optDiv) : 0
      const schwabClose = !d.fromCache && d.totalQty > 0 && d.prevCloseVal > 0 ? d.prevCloseVal / (d.totalQty * optDiv) : 0
      d.currentPrice = schwabPrice
      d.prevClose = schwabClose
    }

    // Order reconciliation is deliberately not part of the cold first-paint
    // gate. The prior unscoped request scanned a year of history across every
    // Schwab account and kept the whole rail on skeletons until it returned.
    // Verify only the tracked refs in the background, then rebucket cards
    // without replacing newer market state.
    const liveOrderEvidencePromise = projects.some(needsOrderEvidence)
      ? fetchTrackedOrderEvidence(projects)
      : null

    const buildCards = (liveOrderEvidenceBySymbol = null) => {
      const latestWorkingOrdersApi = workingOrdersApiRef.current
      const authoritativeOrderSymbol = String(currentSymbolRef.current || '').toUpperCase()
      const authoritativeOrderRows = [
        ...(Array.isArray(latestWorkingOrdersApi?.rawOrders) ? latestWorkingOrdersApi.rawOrders : []),
        ...(Array.isArray(latestWorkingOrdersApi?.orders) ? latestWorkingOrdersApi.orders : []),
      ]
      const getStageContext = (p) => {
        const symbolKey = String(p.symbol || '').toUpperCase()
        const liveData = posMap[symbolKey]
        const context = {
          hasLivePosition: Math.abs(Number(liveData?.totalQty) || 0) > 0,
          hasLinkedTrade: Boolean(p.trade_id && tradeMap[p.trade_id]),
        }
        if (liveOrderEvidenceBySymbol instanceof Map && liveOrderEvidenceBySymbol.has(symbolKey)) {
          context.hasOrderEvidence = liveOrderEvidenceBySymbol.get(symbolKey)
        } else if (latestWorkingOrdersApi?.ready === true && authoritativeOrderSymbol && symbolKey === authoritativeOrderSymbol) {
          context.hasOrderEvidence = collectLiveBrokerOrdersForSymbol(authoritativeOrderRows, symbolKey).length > 0
        } else if (String(p?.stage || '').trim().toLowerCase() === 'working' && !hasTrackableOrderRef(p)) {
          // No scoped broker lookup is possible yet. Preserve the working row
          // until the selected-symbol feed or a tracked-ref reconciliation can
          // make an authoritative yes/no decision.
          context.hasOrderEvidence = true
        }
        return context
      }

      const visibleProjects = projects.filter((p) => {
        return shouldShowPositionListProject(p, getStageContext(p))
      })

      // The rail is asset-scoped, not setup-scoped. One symbol appears once at
      // its highest current lifecycle stage, while every underlying project id
      // travels with the card so the workspace can render its setup cards.
      const symbolGroups = {}
      for (const p of visibleProjects) {
        const displayStage = resolvePositionListStage(p, getStageContext(p))
        const key = String(p.symbol || '').toUpperCase()
        const shouldLead = !symbolGroups[key]
          || assetStageRank(displayStage) > assetStageRank(symbolGroups[key].stage)
        if (!symbolGroups[key]) {
          symbolGroups[key] = {
            symbol: p.symbol, stage: displayStage, projectIds: [], trades: [], created_at: p.created_at,
            brokerSnapshotOnly: Boolean(p.__brokerSnapshotOnly),
            triggerPrice: p.trigger_price ? Number(p.trigger_price) : null,
            stopPrice: p.pattern_failure_price ? Number(p.pattern_failure_price) : null,
            targetPrice: p.sell_target_price ? Number(p.sell_target_price) : null,
            setupSource: p.setup_source || null,
            setupPattern: p.setup_pattern || null,
            totalSetupShares: Array.isArray(p.account_sizing)
              ? p.account_sizing.reduce((s, a) => s + (Number(a.shares) || 0), 0)
              : 0,
            direction: p.direction || 'LONG',
          }
        } else if (shouldLead) {
          Object.assign(symbolGroups[key], {
            symbol: p.symbol,
            stage: displayStage,
            created_at: p.created_at,
            triggerPrice: p.trigger_price ? Number(p.trigger_price) : null,
            stopPrice: p.pattern_failure_price ? Number(p.pattern_failure_price) : null,
            targetPrice: p.sell_target_price ? Number(p.sell_target_price) : null,
            setupSource: p.setup_source || null,
            setupPattern: p.setup_pattern || null,
            totalSetupShares: Array.isArray(p.account_sizing)
              ? p.account_sizing.reduce((s, a) => s + (Number(a.shares) || 0), 0)
              : 0,
            direction: p.direction || 'LONG',
          })
        }
        if (p.id) symbolGroups[key].projectIds.push(p.id)
        symbolGroups[key].brokerSnapshotOnly ||= Boolean(p.__brokerSnapshotOnly)
        if (p.trade_id && tradeMap[p.trade_id]) {
          symbolGroups[key].trades.push(tradeMap[p.trade_id])
        }
      }

      // Build card data
      const cards = Object.values(symbolGroups).map(g => {
        const symbolKey = String(g.symbol || '').toUpperCase()
        const liveData = posMap[symbolKey]
        const { dayChangeDollar, dayChangePct } = computeDayChange(
          liveData?.currentPrice ?? null,
          liveData?.prevClose ?? null,
        )
        const totalShares = liveData && !liveData.fromCache ? liveData.totalQty : 0
        const markPrice = Number(liveData?.currentPrice)
        const lotAccounting = liveData && !liveData.isOption
          ? computeEquityLotAccounting(executionMap[symbolKey] || [], {
              symbol: symbolKey,
              accountNumbers: [...(liveData.accountNumbers || [])],
              markPrice: Number.isFinite(markPrice) && markPrice > 0 ? markPrice : null,
              method: 'LIFO',
            })
          : null
        const lotQtyMatchesBroker = lotAccounting?.available
          && lotAccounting.unmatchedExitQty === 0
          && Math.abs(Number(lotAccounting.remainingQty) - Math.abs(Number(totalShares) || 0)) < 0.0001
        const totalCostBasis = lotQtyMatchesBroker
          ? lotAccounting.remainingBasis
          : (liveData?.totalCostBasis ?? null)
        const unrealizedPnl = liveData
          ? (lotQtyMatchesBroker && lotAccounting.openPnl != null
              ? lotAccounting.openPnl
              : liveData.totalMktVal - liveData.totalCostBasis)
          : null
        const unrealizedPct = totalCostBasis > 0 && unrealizedPnl != null
          ? (unrealizedPnl / totalCostBasis) * 100
          : null
        const dayPnl = liveData?.totalDayPnl ?? null

        // Check if any trade needs action (no stop set, or position has issues)
        const needsAction = g.trades.some(t =>
          t.status === 'open' && (t.stop_loss == null || Number(t.stop_loss) === 0)
        )

        // For active positions: derive entry, stop, shares from trades + schwab data
        const avgEntry = lotQtyMatchesBroker && lotAccounting.remainingAvgPrice > 0
          ? lotAccounting.remainingAvgPrice
          : liveData && !liveData.fromCache && liveData.totalQty > 0
            ? liveData.totalAvgEntryValue / (liveData.totalQty * (liveData.isOption ? 100 : 1))
            : null
        const liveStop = g.trades.length > 0
          ? g.trades.reduce((best, t) => {
              const s = Number(t.stop_loss) || 0
              return s > 0 ? (best > 0 ? Math.min(best, s) : s) : best
            }, 0) || null
          : null
        const daysHeld = g.created_at
          ? Math.floor((Date.now() - new Date(g.created_at).getTime()) / 86400000)
          : null

        return {
          symbol: g.symbol,
          stage: g.stage,
          projectIds: g.projectIds,
          brokerSnapshotOnly: g.brokerSnapshotOnly,
          isGrouped: g.projectIds.length > 1,
          unrealizedPnl,
          unrealizedPct,
          dayPnl,
          needsAction,
          created_at: g.created_at,
          direction: g.direction,
          avgEntry,
          liveStop,
          totalShares,
          daysHeld,
          triggerPrice: g.triggerPrice,
          stopPrice: g.stopPrice,
          targetPrice: g.targetPrice,
          setupSource: g.setupSource,
          setupPattern: g.setupPattern,
          totalSetupShares: g.totalSetupShares,
          isOption: isOCCSymbol(g.symbol),
          isInverse: liveData?.isInverse ?? false,
          totalCostBasis,
          currentPrice: liveData?.currentPrice || null,
          prevClose: liveData?.prevClose || null,
          dayChangeDollar,
          dayChangePct,
        isExtended: marketSession === 'pre' || marketSession === 'post',
          session: marketSession,
        }
      })

      cards.sort((a, b) => {
        switch (sortMode) {
          case 'newest':
            return cardCreatedAtSortValue(b) - cardCreatedAtSortValue(a)
          case 'oldest':
            return cardCreatedAtSortValue(a) - cardCreatedAtSortValue(b)
          case 'pnl_desc':
            return (b.unrealizedPnl ?? -Infinity) - (a.unrealizedPnl ?? -Infinity)
          case 'pnl_asc':
            return (a.unrealizedPnl ?? Infinity) - (b.unrealizedPnl ?? Infinity)
          case 'alpha':
            return a.symbol.localeCompare(b.symbol)
          default:
            return cardCreatedAtSortValue(b) - cardCreatedAtSortValue(a)
        }
      })

      return cards
    }

    const cards = buildCards()

    if (!isLatestRequest()) return
    loadedUserIdRef.current = user.id
    setPositions(previous => rememberPositionSwitcherSnapshot(
      user.id,
      mergeCardsWithStableQuotes(previous, cards),
    ))
    setLoading(false)

    if (liveOrderEvidencePromise) {
      void liveOrderEvidencePromise.then((liveOrderEvidenceBySymbol) => {
        // Sort changes start a new load request, so this generation guard also
        // prevents a delayed reconciliation from restoring an older sort.
        if (!(liveOrderEvidenceBySymbol instanceof Map) || !isLatestRequest()) return
        const reconciledCards = buildCards(liveOrderEvidenceBySymbol)
        setPositions(previous => rememberPositionSwitcherSnapshot(
          user.id,
          mergeCardsPreservingMarketState(previous, reconciledCards),
        ))
      })
    }

  }, [
    user?.id,
    sortMode,
  ])

  useEffect(() => {
    const project = confirmedRestoredIdea
    if (!project?.id || project.user_id !== user?.id || project.stage !== 'idea') return
    // The insert has already succeeded. Publish its card without waiting for
    // unrelated broker reads; the normal refresh reconciles it afterwards.
    loadRequestRef.current += 1
    loadedUserIdRef.current = user.id
    setPositions(previous => {
      const existing = previous.find(card => card.symbol === project.symbol)
      const projectIds = [...new Set([...(existing?.projectIds || []), project.id])]
      const card = existing ? { ...existing, projectIds, isGrouped: projectIds.length > 1 } : {
        symbol: project.symbol, stage: 'idea', projectIds, isGrouped: false,
        created_at: project.created_at, direction: project.direction,
        triggerPrice: project.trigger_price, stopPrice: project.pattern_failure_price,
        targetPrice: project.sell_target_price, setupPattern: project.setup_pattern,
        setupSource: project.setup_source, totalShares: 0,
        totalSetupShares: (project.account_sizing || []).reduce((sum, row) => sum + (Number(row.shares) || 0), 0),
      }
      return rememberPositionSwitcherSnapshot(user.id, [card, ...previous.filter(row => row.symbol !== project.symbol)])
    })
    setLoading(false)
    onRestoredIdeaPublished?.()
  }, [confirmedRestoredIdea, onRestoredIdeaPublished, user?.id])

  useEffect(() => { loadPositions() }, [loadPositions, refreshKey])

  // The position rail owns its lifecycle read and must refresh independently
  // when a completed account sync changes project stages. Routing this only
  // through the selected workspace's broker refresh can leave an already-open
  // tab on its prior memory snapshot if that unrelated refresh is slow or
  // fails. The generation guard inside loadPositions prevents stale overlap.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const handleSchwabSyncComplete = () => { loadPositions() }
    window.addEventListener(SCHWAB_SYNC_COMPLETE_EVENT, handleSchwabSyncComplete)
    return () => window.removeEventListener(SCHWAB_SYNC_COMPLETE_EVENT, handleSchwabSyncComplete)
  }, [loadPositions])

  // Notifications wake an owner-scoped read; their text/quantity never becomes
  // position authority. Listen for every asset, independently of the chart.
  useEffect(() => {
    if (brokerNotificationIdsRef.current.userId !== user?.id) {
      brokerNotificationIdsRef.current = { userId: user?.id, ids: new Set() }
    }
    if (!user?.id) return
    const ids = (notifications || [])
      .filter(notification => notification?.serverBacked === true && notification?.symbol)
      .flatMap(notification => notification.sourceNotificationIds?.length
        ? notification.sourceNotificationIds
        : [notification.id])
      .filter(Boolean)
    const seen = brokerNotificationIdsRef.current.ids
    const hasNewEvent = ids.some(id => !seen.has(id))
    ids.forEach(id => seen.add(id))
    if (hasNewEvent) void loadPositions()
  }, [notifications, user?.id, loadPositions])

  useEffect(() => {
    if (!user?.id) return undefined
    return subscribeBrowserResume(() => { void loadPositions() })
  }, [user?.id, loadPositions])

  const activeManualList = activeStageTab === 'watchlists'
    ? durableAssetLists.find(list => list.listId === activeManualListId) || null
    : null

  const addCurrentAssetToSelectedWatchlist = useCallback(async () => {
    if (!activeManualList) {
      setAssetListNotice({ tone: 'error', text: 'Select a custom watchlist first' })
      return { ok: false, reason: 'no_selected_watchlist' }
    }
    const normalized = String(currentSymbol || '').trim().toUpperCase()
    if (!normalized) {
      setAssetListNotice({ tone: 'error', text: 'Open an asset before adding it to a watchlist' })
      return { ok: false, reason: 'no_current_asset' }
    }

    setAssetListNotice(null)
    try {
      const response = await searchPositionAssetListInstruments(normalized)
      const candidate = (response.results || []).find(result => (
        String(result?.symbol || '').trim().toUpperCase() === normalized
      ))
      if (!candidate) {
        setAssetListNotice({
          tone: 'error',
          text: `${normalized} is not available as a verified U.S. stock or ETF`,
        })
        return { ok: false, reason: 'verified_asset_unavailable' }
      }
      const added = await addCandidateToActiveManualList(candidate, { showRailNotice: true })
      return added ? { ok: true } : { ok: false, reason: 'watchlist_add_failed' }
    } catch (error) {
      setAssetListNotice({
        tone: 'error',
        text: error?.message || `Could not add ${normalized} to ${activeManualList.name}`,
      })
      return { ok: false, reason: 'watchlist_add_failed' }
    }
  }, [activeManualList, addCandidateToActiveManualList, currentSymbol])

  const activeAssetListViewRef = activeManualList
    ? { kind: 'manual-list', listId: activeManualList.listId }
    : activeStageTab === 'all'
      ? { kind: 'all-assets' }
      : { kind: 'lifecycle', lifecycle: activeStageTab === 'idea' ? 'ideas' : activeStageTab === 'closed' ? 'recently-closed' : activeStageTab }
  const activeAssetListViewKey = assetListViewKey(activeAssetListViewRef)

  useEffect(() => {
    let cancelled = false
    if (warmRows) {
      setDurableAssetListLoading(false)
      return () => { cancelled = true }
    }
    setLocalAssetListRows([])
    setDurableAssetListLoading(true)
    if (!user?.id || typeof supabase.rpc !== 'function') {
      setDurableAssetListLoading(false)
      return () => { cancelled = true }
    }
    readPositionAssetListView(activeAssetListViewRef)
      .then(rows => {
        if (!cancelled) setDurableAssetListRows(rows)
      })
      .catch(() => {
        if (!cancelled) setAssetListPersistenceError('Could not load the canonical Asset List view.')
      })
      .finally(() => {
        if (!cancelled) setDurableAssetListLoading(false)
      })
    return () => { cancelled = true }
  }, [user?.id, activeAssetListViewKey, warmRows]) // eslint-disable-line react-hooks/exhaustive-deps

  const instrumentIdBySymbol = useMemo(() => new Map(
    durableAssetListRows
      .filter(row => row?.instrumentId && row?.symbol)
      .map(row => [String(row.symbol).toUpperCase(), row.instrumentId]),
  ), [durableAssetListRows])
  const instrumentIdsKey = [...new Set(instrumentIdBySymbol.values())].sort().join('|')
  useEffect(() => {
    const releases = instrumentIdsKey
      .split('|')
      .filter(Boolean)
      .map(instrumentId => ibkrInstrumentQuoteStore.retain(instrumentId))
    return () => releases.forEach(release => release?.())
  }, [instrumentIdsKey])

  const quoteSymbolsKey = [...new Set([
    ...positions
      .filter(card => (
        !durableAssetListLoading
        && !instrumentIdBySymbol.has(String(card.symbol || '').toUpperCase())
      ))
      .map(card => card.symbol),
    ...durableAssetListRows.filter(row => !row.instrumentId).map(row => row.symbol),
  ].filter(Boolean))].join('|')
  useEffect(() => {
    const retained = new Set()
    const releases = []
    for (const symbol of quoteSymbolsKey.split('|').filter(Boolean)) {
      const request = positionQuoteRequest(symbol)
      const key = `${request.streamSymbol}|${JSON.stringify(request.params)}`
      if (!request.streamSymbol || retained.has(key)) continue
      retained.add(key)
      releases.push(ibkrQuoteSnapshotStore.retain(request.streamSymbol, request.params))
    }
    return () => releases.forEach(release => release?.())
  }, [quoteSymbolsKey])

  // Live IBKR WebSocket streaming for card prices
  useEffect(() => {
    if (positions.length === 0) return

    const symbols = [...new Set(
      positions
        .filter(card => (
          !durableAssetListLoading
          && !instrumentIdBySymbol.has(String(card.symbol || '').toUpperCase())
        ))
        .map(card => card.symbol),
    )]
    const unsubs = []

    for (const sym of symbols) {
      const isOpt = isOCCSymbol(sym)
      const occ = isOpt ? parseOCC(sym) : null
      const streamSymbol = occ ? occ.underlying : sym
      const streamParams = occ
        ? { secType: 'OPT', expiry: occ.expiry, strike: String(occ.strike), right: occ.right }
        : {}

      ibkrQuoteStream.subscribe(streamSymbol, (tick) => {
        if (tick.field === 'reset') {
          setPositions(prev => prev.map(card => card.symbol === sym
            ? {
                ...card,
                currentPrice: card._brokerCurrentPrice ?? card.currentPrice,
                unrealizedPnl: card._brokerUnrealizedPnl ?? card.unrealizedPnl,
                unrealizedPct: card._brokerUnrealizedPct ?? card.unrealizedPct,
                _last: null, _sessionClose: null, _sessionOpen: null, _lastRth: null, _mark: null, _bid: null, _ask: null,
                _quoteSession: null, _quoteContract: null, freshnessReason: null,
                session: marketSession,
                isExtended: quoteSessionPresentation(marketSession) != null,
              }
            : card))
          return
        }
        if (!['last', 'close', 'sessionClose', 'open', 'lastRth', 'mark', 'bid', 'ask'].includes(tick.field) || !(tick.value > 0)) return
        setPositions(prev => prev.map(card => {
          if (card.symbol !== sym) return card
          const last = tick.field === 'last' ? tick.value : card._last
          const prevClose = tick.field === 'close' ? tick.value : card.prevClose
          const sessionClose = tick.field === 'sessionClose' ? tick.value : card._sessionClose
          const sessionOpen = tick.field === 'open' ? tick.value : card._sessionOpen
          const lastRth = tick.field === 'lastRth' ? tick.value : card._lastRth
          const mark = tick.field === 'mark' ? tick.value : card._mark
          const bid = tick.field === 'bid' ? tick.value : card._bid
          const ask = tick.field === 'ask' ? tick.value : card._ask
          const currentPrice = selectPositionQuotePrice(
            { last, close: prevClose, sessionClose, lastRth, bid, ask },
            tick.session || card._quoteSession || marketSession,
            isOpt ? 'OPT' : 'STK',
          ) ?? card.currentPrice
          const { dayChangeDollar, dayChangePct } = computeDayChange(currentPrice, prevClose)
          const multiplier = isOpt ? 100 : 1
          const liveMktVal = card.totalShares > 0 ? currentPrice * card.totalShares * multiplier : null
          const unrealizedPnl = liveMktVal != null && card.totalCostBasis != null
            ? liveMktVal - card.totalCostBasis
            : card.unrealizedPnl
          const unrealizedPct = card.totalCostBasis && card.totalCostBasis > 0
            ? (unrealizedPnl / card.totalCostBasis) * 100
            : card.unrealizedPct
          return {
            ...card,
            _brokerCurrentPrice: card._brokerCurrentPrice ?? card.currentPrice,
            _brokerUnrealizedPnl: card._brokerUnrealizedPnl ?? card.unrealizedPnl,
            _brokerUnrealizedPct: card._brokerUnrealizedPct ?? card.unrealizedPct,
            currentPrice, prevClose, _last: last, _sessionClose: sessionClose, _sessionOpen: sessionOpen, _lastRth: lastRth, _mark: mark,
            _bid: bid, _ask: ask, _quoteSession: tick.session || card._quoteSession,
            _quoteContract: tick.contract || card._quoteContract,
            freshness: tick.freshness || card.freshness || null,
            freshnessReason: tick.freshnessReason ?? null,
            receivedTs: tick.receivedTs || tick.ts || card.receivedTs || null,
            session: tick.session || card._quoteSession || card.session,
            isExtended: quoteSessionPresentation(tick.session || card._quoteSession || card.session) != null,
            dayChangeDollar, dayChangePct, unrealizedPnl, unrealizedPct,
          }
        }))
      }, streamParams).then(unsub => { unsubs.push(unsub) }).catch(() => {})
    }

    return () => { unsubs.forEach(fn => { if (fn) fn() }) }
  }, [positions.map(p => p.symbol).join(','), marketSession, instrumentIdsKey, durableAssetListLoading]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeStageTab === 'idea') return
    setIdeaSelectMode(false)
    setSelectedIdeaKeys(new Set())
  }, [activeStageTab])

  useEffect(() => {
    const visibleIdeaKeys = new Set(positions.filter(card => stageTabFor(card.stage) === 'idea').map(ideaCardKey))
    setSelectedIdeaKeys(prev => {
      let changed = false
      const next = new Set()
      for (const key of prev) {
        if (visibleIdeaKeys.has(key)) next.add(key)
        else changed = true
      }
      return changed ? next : prev
    })
  }, [positions])

  const handleDeleteIdeaCards = useCallback(async (cardsToDelete) => {
    if (!user?.id || ideaRemovalRef.current) return
    const ownerId = user.id
    const cards = (cardsToDelete || []).filter(card => stageTabFor(card?.stage) === 'idea')
    const projectIds = [...new Set(cards.flatMap(card => card.projectIds || []).filter(Boolean))]
    if (!projectIds.length) return
    const operation = { cancelled: false, cancel: null }
    ideaRemovalRef.current = operation
    const isCurrent = () => !operation.cancelled && ideaOwnerRef.current === ownerId && ideaRemovalRef.current === operation
    clearTimeout(ideaNoticeTimerRef.current)
    setIdeaDeletePending(true)
    setIdeaDeleteNotice(null)

    try {
      const deletedIds = await deleteAssetIdeaProjects(supabase, ownerId, projectIds,
        () => isCurrent() ? biometricGate.verify() : Promise.resolve({ verified: false }),
        () => {
          if (!isCurrent()) return false
          setPendingIdeaIds(new Set(projectIds))
          return new Promise(resolve => {
            let settled = false
            const finish = (commit) => {
              if (settled) return
              settled = true
              clearTimeout(timer)
              operation.cancel = null
              if (isCurrent()) {
                if (!commit) setPendingIdeaIds(new Set())
                setIdeaDeleteNotice(notice => notice ? { ...notice, fading: true } : null)
                ideaNoticeTimerRef.current = setTimeout(() => setIdeaDeleteNotice(null), 250)
              }
              resolve(commit && isCurrent())
            }
            const timer = setTimeout(() => finish(true), 2500)
            operation.cancel = () => finish(false)
            setIdeaDeleteNotice({
              tone: 'success',
              text: cards.length === 1 ? `${cards[0].symbol} removed from Ideas` : `${cards.length} ideas removed`,
              undo: () => finish(false),
            })
          })
        })
      if (!isCurrent() || deletedIds === null) return
      const ids = new Set(deletedIds)
      setPositions(prev => {
        const next = prev.filter(card => !card.projectIds.length || !card.projectIds.every(id => ids.has(id)))
        positionSwitcherSnapshotByUser.set(ownerId, next)
        return next
      })
      setSelectedIdeaKeys(prev => {
        const next = new Set(prev)
        cards.filter(card => card.projectIds.every(id => ids.has(id))).forEach(card => next.delete(ideaCardKey(card)))
        return next
      })
      if (deletedIds.length !== projectIds.length) {
        await loadPositions()
        if (!isCurrent()) return
        clearTimeout(ideaNoticeTimerRef.current)
        setIdeaDeleteNotice({ tone: 'error', text: 'Some ideas changed before removal. Review them and try again.' })
      }
    } catch (err) {
      if (!isCurrent()) return
      clearTimeout(ideaNoticeTimerRef.current)
      setIdeaDeleteNotice({ tone: 'error', text: err?.message || 'Could not remove idea(s)' })
    } finally {
      if (isCurrent()) {
        ideaRemovalRef.current = null
        setPendingIdeaIds(new Set())
        setIdeaDeletePending(false)
      }
    }
  }, [user?.id, biometricGate, loadPositions])

  const visiblePositions = useMemo(() => positions.filter(card => !(stageTabFor(card.stage) === 'idea'
    && card.projectIds?.length && card.projectIds.every(id => pendingIdeaIds.has(id)))), [positions, pendingIdeaIds])
  const tabCounts = visiblePositions.reduce((acc, card) => {
    const tab = stageTabFor(card.stage)
    acc[tab] = (acc[tab] || 0) + 1
    return acc
  }, {})
  tabCounts.watchlists = durableAssetListRows.length
  tabCounts.all = visiblePositions.length
  const positionStageTabs = POSITION_STAGE_TABS

  const authoritativePositions = useMemo(() => visiblePositions.map(card => {
    const request = positionQuoteRequest(card.symbol)
    const instrumentId = instrumentIdBySymbol.get(String(card.symbol || '').toUpperCase())
    const snapshot = instrumentId
      ? ibkrInstrumentQuoteStore.getSnapshot(instrumentId)
      : ibkrQuoteSnapshotStore.getSnapshot(request.streamSymbol, request.params)
    if (snapshot) {
      const displaySession = resolveQuoteDisplaySession(snapshot, sessionClock) || marketSession
      const displaySnapshot = displaySession === snapshot.session
        ? snapshot
        : { ...snapshot, session: displaySession }
      return applyAuthoritativeQuoteSnapshot(card, displaySnapshot, marketSession)
    }
    return card
  }), [
    visiblePositions,
    marketSession,
    quoteSnapshotVersion,
    instrumentQuoteVersion,
    instrumentIdBySymbol,
    sessionClock,
  ])

  const durableManualCards = useMemo(() => durableAssetListRows.map((row) => {
    const position = authoritativePositions.find(card => card.symbol === row.symbol)
    if (position) {
      return {
        ...position,
        rowKey: row.rowKey,
        manualMembershipId: row.sources?.find(source => source.kind === 'manual-list')?.membershipId || null,
      }
    }
    const card = {
      rowKey: row.rowKey,
      manualMembershipId: row.sources?.find(source => source.kind === 'manual-list')?.membershipId || null,
      symbol: row.symbol,
      displayName: row.displayName,
      assetType: row.assetType,
      stage: 'watchlist',
      projectIds: [],
      currentPrice: null,
      prevClose: null,
      dayChangePct: null,
      totalShares: null,
      unrealizedPnl: null,
      freshness: 'unavailable',
    }
    const snapshot = row.instrumentId
      ? ibkrInstrumentQuoteStore.getSnapshot(row.instrumentId)
      : ibkrQuoteSnapshotStore.getSnapshot(
          positionQuoteRequest(row.symbol).streamSymbol,
          positionQuoteRequest(row.symbol).params,
        )
    return snapshot ? applyAuthoritativeQuoteSnapshot(card, snapshot, marketSession) : card
  }), [authoritativePositions, durableAssetListRows, marketSession, quoteSnapshotVersion, instrumentQuoteVersion])

  const filteredPositions = activeManualList
    ? durableManualCards
    : activeStageTab === 'all'
    ? authoritativePositions
    : activeStageTab === 'watchlists'
      ? []
      : authoritativePositions.filter(card => stageTabFor(card.stage) === activeStageTab)
  const classificationAssets = new Map(durableAssetListRows.map(row => [row.symbol, row]))
  const classificationRows = filteredPositions.map(card => ({
    symbol: card.symbol,
    assetProfile: classificationAssets.get(card.symbol)?.assetProfile,
    assetType: classificationAssets.get(card.symbol)?.assetType
      || card.assetType || (card.isOption || isOCCSymbol(card.symbol) ? 'option' : 'equity'),
  }))
  const equityPriceDisplays = useEquityPriceDisplays(filteredPositions)
  const assetListPositions = filteredPositions.map(card => ({
    ...card,
    _equityDisplay: equityPriceDisplays[String(card.symbol || '').toUpperCase()],
    _quoteSession: resolvePositionCardAssetListSession(card, sessionClock, marketSession),
  }))
  const filteredCardKeys = filteredPositions.map(ideaCardKey).join('::')
  const loadedVisibleCardKey = useMemo(() => {
    const loadedCard = filteredPositions.find(card => (
      card.projectIds.some(id => currentIds.has(id)) || card.symbol === currentSymbol
    ))
    return loadedCard ? ideaCardKey(loadedCard) : null
  }, [currentIds, currentSymbol, filteredCardKeys]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleClick = useCallback((card) => {
    setHighlightedCardKey(ideaCardKey(card))
    if (card.projectIds.some(id => currentIds.has(id))) return
    if (card.stage === 'watchlist' && typeof onOpenWatchlist === 'function') {
      onOpenWatchlist(card)
      if (onClose) onClose()
      return
    }
    if (card.projectIds.length === 0) {
      if (typeof onOpenSymbol === 'function') {
        onOpenSymbol(card.symbol)
      } else {
        navigate(buildPositionWorkspaceTarget({ symbol: card.symbol }))
      }
      if (onClose) onClose()
      return
    }
    if (typeof onOpenPosition === 'function') {
      onOpenPosition(card)
      if (onClose) onClose()
      return
    }
    navigate(buildPositionWorkspaceTarget({ symbol: card.symbol, projectIds: card.projectIds }))
    if (onClose) onClose()
  }, [currentIds, navigate, onClose, onOpenPosition, onOpenSymbol, onOpenWatchlist])

  const handleAssetListCard = useCallback((card) => {
    if (ideaSelectMode && stageTabFor(card.stage) === 'idea') {
      toggleIdeaCardSelection(card)
      return
    }
    handleClick(card)
  }, [handleClick, ideaSelectMode, toggleIdeaCardSelection])

  useEffect(() => {
    if (filteredPositions.length === 0) {
      setHighlightedCardKey(null)
      return
    }
    setHighlightedCardKey(current => {
      if (current && filteredPositions.some(card => ideaCardKey(card) === current)) return current
      const activeCard = filteredPositions.find(card => (
        card.projectIds.some(id => currentIds.has(id)) || card.symbol === currentSymbol
      ))
      return activeCard ? ideaCardKey(activeCard) : null
    })
  }, [activeStageTab, currentIds, currentSymbol, filteredCardKeys]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!highlightedCardKey || highlightedCardKey === loadedVisibleCardKey) return undefined
    const timer = window.setTimeout(() => {
      setHighlightedCardKey(loadedVisibleCardKey)
    }, RAIL_HIGHLIGHT_IDLE_MS)
    return () => window.clearTimeout(timer)
  }, [highlightedCardKey, loadedVisibleCardKey])

  const moveRailHighlight = useCallback((delta) => {
    const visibleCards = visibleAssetCardsRef.current
    if (visibleCards.length === 0) return { ok: false, reason: 'no_rail_items' }
    setHighlightedCardKey(current => {
      const currentIndex = visibleCards.findIndex(card => ideaCardKey(card) === current)
      let startIndex = currentIndex
      if (currentIndex < 0) startIndex = delta > 0 ? -1 : visibleCards.length
      const nextIndex = (startIndex + delta + visibleCards.length) % visibleCards.length
      return ideaCardKey(visibleCards[nextIndex])
    })
    return { ok: true }
  }, [filteredCardKeys]) // eslint-disable-line react-hooks/exhaustive-deps

  const openHighlightedRailItem = useCallback(() => {
    const visibleCards = visibleAssetCardsRef.current
    const card = visibleCards.find(item => ideaCardKey(item) === highlightedCardKey)
    if (!card) {
      return { ok: false, reason: visibleCards.length === 0 ? 'no_rail_items' : 'no_rail_selection' }
    }
    handleClick(card)
    return { ok: true }
  }, [currentIds, currentSymbol, filteredCardKeys, handleClick, highlightedCardKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const switchAsset = useCallback((delta) => {
    const visibleCards = visibleAssetCardsRef.current
    if (visibleCards.length === 0) return { ok: false, reason: 'no_rail_items' }
    const currentIndex = visibleCards.findIndex(card => (
      card.projectIds.some(id => currentIds.has(id)) || card.symbol === currentSymbol
    ))
    const highlightedIndex = visibleCards.findIndex(card => ideaCardKey(card) === highlightedCardKey)
    const startIndex = currentIndex >= 0 ? currentIndex : highlightedIndex >= 0 ? highlightedIndex : (delta > 0 ? -1 : visibleCards.length)
    const nextIndex = (startIndex + delta + visibleCards.length) % visibleCards.length
    handleClick(visibleCards[nextIndex])
    return { ok: true }
  }, [currentIds, currentSymbol, filteredCardKeys, handleClick, highlightedCardKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const switchAssetRef = useRef(switchAsset)
  switchAssetRef.current = switchAsset

  const handleColumnKeyDown = useCallback((event) => {
    if (event.target !== event.currentTarget || event.altKey || event.ctrlKey || event.metaKey) return
    if (event.key === 'Tab') {
      event.preventDefault()
      event.stopPropagation()
      moveRailHighlight(event.shiftKey ? -1 : 1)
      return
    }
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault()
      event.stopPropagation()
      moveRailHighlight(event.key === 'ArrowDown' ? 1 : -1)
      return
    }
    if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()
      openHighlightedRailItem()
      return
    }
    if (event.key === '[' || event.key === ']') {
      const currentIndex = positionStageTabs.indexOf(activeStageTab)
      const direction = event.key === ']' ? 1 : -1
      const nextIndex = (Math.max(0, currentIndex) + direction + positionStageTabs.length) % positionStageTabs.length
      event.preventDefault()
      event.stopPropagation()
      setActiveStageTab(positionStageTabs[nextIndex])
      return
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      event.currentTarget.blur()
    }
  }, [activeStageTab, moveRailHighlight, openHighlightedRailItem, positionStageTabs])

  useEffect(() => {
    const unregisterAssetPrevious = registerCommandHandler('position.asset.previous', () => switchAssetRef.current(-1))
    const unregisterAssetNext = registerCommandHandler('position.asset.next', () => switchAssetRef.current(1))
    const unregisterPrevious = registerCommandHandler('position.rail.previous', () => moveRailHighlight(-1))
    const unregisterNext = registerCommandHandler('position.rail.next', () => moveRailHighlight(1))
    const unregisterOpen = registerCommandHandler('position.rail.open', openHighlightedRailItem)
    const unregisterAddCurrentAsset = registerCommandHandler(
      'position.watchlist.add_current_asset',
      addCurrentAssetToSelectedWatchlist,
    )
    return () => {
      unregisterAssetPrevious()
      unregisterAssetNext()
      unregisterPrevious()
      unregisterNext()
      unregisterOpen()
      unregisterAddCurrentAsset()
    }
  }, [addCurrentAssetToSelectedWatchlist, moveRailHighlight, openHighlightedRailItem, registerCommandHandler, switchAsset])

  useEffect(() => {
    if (!highlightedCardKey) return
    const frame = requestAnimationFrame(() => {
      cardRefs.current.get(highlightedCardKey)?.scrollIntoView?.({ block: 'nearest' })
    })
    return () => cancelAnimationFrame(frame)
  }, [highlightedCardKey])

  const activeConfigurationKey = assetListViewKey(activeAssetListViewRef)
  const activeConfigurationRecord = assetListConfigurations[activeConfigurationKey]
  const allowedAssetListColumns = positionAssetListColumnsForView(activeAssetListViewRef)
  const activeAssetListConfiguration = normalizePositionAssetListConfiguration(
    activeAssetListViewRef,
    activeConfigurationRecord?.configuration || defaultPositionAssetListConfiguration(activeAssetListViewRef),
  )

  const watchlistClassifications = useWatchlistClassifications(
    user?.id, classificationRows, activeAssetListConfiguration.groupBySector,
  )

  const persistAssetListConfiguration = useCallback(async (nextConfiguration) => {
    if (!user?.id || assetListSettingsOwner !== user.id || configurationSaveRef.current) return
    const ownerId = user.id
    const operation = {}
    configurationSaveRef.current = operation
    setConfigurationSaving(true)
    const viewRef = activeAssetListViewRef
    const key = assetListViewKey(viewRef)
    const previous = assetListConfigurations[key]
    const expectedVersion = previous?.version || 0
    setAssetListConfigurations(current => ({
      ...current,
      [key]: { version: expectedVersion, configuration: nextConfiguration },
    }))
    setAssetListPersistenceError(null)
    try {
      const version = await savePositionAssetListConfiguration(viewRef, expectedVersion, nextConfiguration)
      if (importOwnerRef.current !== ownerId || configurationSaveRef.current !== operation) return
      setAssetListConfigurations(current => ({ ...current, [key]: { version, configuration: nextConfiguration } }))
    } catch (error) {
      if (importOwnerRef.current !== ownerId || configurationSaveRef.current !== operation) return
      setAssetListConfigurations(current => {
        const restored = { ...current }
        if (previous) restored[key] = previous
        else delete restored[key]
        return restored
      })
      const message = String(error?.message || '').trim()
      setAssetListPersistenceError(
        /version conflict/i.test(message)
          ? 'Asset List settings changed elsewhere. Reload to reconcile the latest version.'
          : `Could not save Asset List settings${message ? `: ${message}` : '.'}`,
      )
    } finally {
      if (configurationSaveRef.current === operation) {
        configurationSaveRef.current = null
        setConfigurationSaving(false)
      }
    }
  }, [activeConfigurationKey, assetListConfigurations, assetListSettingsOwner, user?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleAssetListSortChange = useCallback((sort) => {
    if (sort.field === 'manual') setSortMode('newest')
    if (activeStageTab === 'all') void ideaOrder.save(sort.field === 'manual' ? { mode: 'manual', rowIds: ideaOrder.value?.rowIds || [] } : { mode: 'column', sort })
    void listOrder.save(sort.field === 'manual'
      ? { mode: 'manual', rowIds: listOrder.value?.rowIds || [] }
      : { mode: 'column', sort })
  }, [listOrder.save, listOrder.value, ideaOrder.save, ideaOrder.value, activeStageTab])

  const handleAssetListColumnToggle = useCallback((columnKey) => {
    if (columnKey === 'symbol') return
    const selected = new Set(activeAssetListConfiguration.columns || allowedAssetListColumns)
    if (selected.has(columnKey)) selected.delete(columnKey)
    else selected.add(columnKey)
    selected.add('symbol')
    const columns = allowedAssetListColumns.filter(key => selected.has(key))
    void persistAssetListConfiguration({ ...activeAssetListConfiguration, columns })
  }, [activeAssetListConfiguration, allowedAssetListColumns, persistAssetListConfiguration])

  const handleLifecycleSectionToggle = useCallback((groupId, enabled) => {
    const key = groupId === 'recently-closed' ? 'recentlyClosed' : groupId
    const nextSections = { ...activeAssetListConfiguration.lifecycleSections, [key]: enabled }
    setLifecycleSections(nextSections)
    void persistAssetListConfiguration({ ...activeAssetListConfiguration, lifecycleSections: nextSections })
  }, [activeAssetListConfiguration, persistAssetListConfiguration])

  const handleRemoveDurableManualAsset = useCallback(async (card) => {
    if (!activeManualList || !card.manualMembershipId) return
    setAssetListPersistenceError(null)
    try {
      const version = await removePositionAssetListMembership(
        activeManualList.listId,
        card.manualMembershipId,
        Number(activeManualList.version),
      )
      setDurableAssetLists(current => current.map(list => (
        list.listId === activeManualList.listId ? { ...list, version } : list
      )))
      setDurableAssetListRows(current => current.filter(row => row.rowKey !== card.rowKey))
    } catch {
      setAssetListPersistenceError(`Could not remove ${card.symbol} from ${activeManualList.name}.`)
    }
  }, [activeManualList])

  const handleSaveWatchlistName = useCallback(async () => {
    const name = watchlistNameDraft.trim()
    if (!name || watchlistMutationPending) return
    setWatchlistMutationPending(true)
    setAssetListPersistenceError(null)
    try {
      if (watchlistEditorMode === 'create') {
        const listId = await createPositionAssetList(name)
        const state = await readPositionAssetListState()
        setDurableAssetLists(state.lists)
        setActiveManualListId(listId)
        setActiveStageTab('watchlists')
      } else if (watchlistEditorMode === 'rename' && activeManualList) {
        const version = await renamePositionAssetList(
          activeManualList.listId,
          name,
          Number(activeManualList.version),
        )
        setDurableAssetLists(current => current.map(list => (
          list.listId === activeManualList.listId ? { ...list, name, version } : list
        )))
      }
      closeWatchlistEditor()
    } catch (error) {
      setAssetListPersistenceError(watchlistNameErrorMessage(error, watchlistEditorMode))
    } finally {
      setWatchlistMutationPending(false)
    }
  }, [activeManualList, closeWatchlistEditor, watchlistEditorMode, watchlistMutationPending, watchlistNameDraft])

  const handleDeleteWatchlist = useCallback(async () => {
    if (!activeManualList || watchlistMutationPending) return
    setWatchlistMutationPending(true)
    setAssetListPersistenceError(null)
    try {
      await archivePositionAssetList(activeManualList.listId, Number(activeManualList.version))
      const remaining = durableAssetLists.filter(list => list.listId !== activeManualList.listId)
      setDurableAssetLists(remaining)
      setActiveManualListId(remaining[0]?.listId || null)
      setDurableAssetListRows([])
      setWatchlistEditorMode(null)
    } catch (error) {
      setAssetListPersistenceError(error?.message || `Could not delete ${activeManualList.name}.`)
    } finally {
      setWatchlistMutationPending(false)
    }
  }, [activeManualList, durableAssetLists, watchlistMutationPending])

  if (loading) {
    return (
      <div className="p-3">
        <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-3">Positions</div>
        <div className="space-y-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-12 rounded-lg bg-canvas2 animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  const selectedIdeaCards = filteredPositions.filter(card => selectedIdeaKeys.has(ideaCardKey(card)))
  const showIdeaControls = activeStageTab === 'idea' && filteredPositions.length > 0
  const currentAssetListLabel = activeManualList?.name || POSITION_STAGE_LABELS[activeStageTab] || 'Asset List'
  const selectLifecycleList = (stage) => {
    setActiveStageTab(stage)
    setShowListMenu(false)
    setAssetListPersistenceError(null)
  }
  const selectManualList = (listId) => {
    setActiveManualListId(listId)
    setActiveStageTab('watchlists')
    setShowListMenu(false)
    setAssetListPersistenceError(null)
  }
  const assetListView = activeManualList
    ? { id: `manual-${activeManualList.listId}`, kind: 'manual-list', listId: activeManualList.listId, label: activeManualList.name }
    : activeStageTab === 'all'
    ? { id: 'all-assets', kind: 'all-assets', label: 'All Assets' }
    : activeStageTab === 'watchlists'
      ? { id: 'custom-watchlists', kind: 'manual-list', label: 'Watchlists' }
      : {
          id: `lifecycle-${activeStageTab}`,
          kind: 'lifecycle',
          label: activeStageTab === 'idea'
            ? 'Ideas'
            : `${activeStageTab[0].toUpperCase()}${activeStageTab.slice(1)}`,
        }
  const highlightedCard = filteredPositions.find(card => ideaCardKey(card) === highlightedCardKey)
  const assetListError = positionLoadError

  return (
    <div
      role="region"
      aria-label="Position workspace left column"
      tabIndex={-1}
      data-command-target="position.left_column.focus"
      onKeyDown={handleColumnKeyDown}
      className="px-1 py-2 flex flex-col h-full outline-none focus-visible:ring-2 focus-visible:ring-ink/40 focus-visible:ring-inset"
    >
      <div className="relative mb-2 flex items-center gap-1" ref={listMenuRef}>
        <button
          type="button"
          aria-label={`Current Asset List: ${currentAssetListLabel}`}
          aria-expanded={showListMenu}
          aria-haspopup="menu"
          onClick={() => setShowListMenu(value => !value)}
          className="flex min-w-0 flex-1 items-center gap-1.5 rounded px-1.5 py-1 text-left text-ink hover:bg-canvas2"
        >
          <span className={`h-4 w-2 shrink-0 ${activeManualList ? 'rounded-sm bg-inkMuted' : activeStageTab === 'active' ? 'bg-forest' : activeStageTab === 'working' ? 'bg-amber-500' : activeStageTab === 'idea' ? 'bg-blue-600' : activeStageTab === 'closed' ? 'bg-ember' : 'rounded-full bg-inkMuted'}`} style={activeStageTab !== 'all' ? { clipPath: 'polygon(0 0, 100% 0, 64% 50%, 100% 100%, 0 100%)' } : undefined} />
          <span className="truncate font-sans text-sm font-semibold">{currentAssetListLabel}</span>
          <ChevronDown size={13} className="shrink-0 text-inkMuted" />
        </button>
        {showIdeaControls && (
          <button
            type="button"
            aria-label={ideaSelectMode ? 'Cancel idea selection' : 'Select ideas'}
            onClick={() => setIdeaSelectMode(value => !value)}
            className="rounded p-1.5 text-inkMuted hover:bg-canvas2 hover:text-ink"
          >
            {ideaSelectMode ? <X size={15} /> : <CheckSquare size={15} />}
          </button>
        )}
        <button
          type="button"
          aria-label="Add symbol"
          title={activeManualList ? `Add symbol to ${activeManualList.name}` : 'Add symbol'}
          onClick={() => {
            setOpenError(null)
            setSymbolSearchMode('add')
            setInstrumentSearchFilter('all')
            setShowSymbolSearch(true)
          }}
          className="rounded p-1.5 text-inkMuted hover:bg-canvas2 hover:text-ink"
        >
          <Plus size={17} />
        </button>
        <button
          type="button"
          aria-label="Import list from CSV"
          title="Import list from CSV"
          disabled={!user || watchlistMutationPending}
          onClick={() => setShowCsvImport(true)}
          className="rounded p-1.5 text-inkMuted hover:bg-canvas2 hover:text-ink disabled:opacity-40"
        >
          <Upload size={17} />
        </button>
        {showCsvImport && user && <AssetListImportModal
          key={user.id}
          lists={durableAssetLists}
          selectedListId={activeManualList?.listId}
          onClose={() => setShowCsvImport(false)}
          onImported={async listId => {
            const [state, rows] = await Promise.all([
              readPositionAssetListState(),
              readPositionAssetListView({ kind: 'manual-list', listId }),
            ])
            if (importOwnerRef.current !== user.id) return
            setDurableAssetLists(state.lists)
            setActiveManualListId(listId)
            setActiveStageTab('watchlists')
            setLocalAssetListRows(rows)
            priceSession?.updateView(`manual-list:${listId}`, rows)
          }}
        />}
        {historyList && user && <AssetListImportHistoryModal key={`${user.id}:${historyList.listId}`} list={historyList} onClose={() => setHistoryList(null)} />}
        <div className="relative" ref={sortMenuRef}>
          <button
            type="button"
            aria-label="Asset List settings"
            aria-expanded={showSortMenu}
            aria-haspopup="menu"
            onClick={() => setShowSortMenu(value => !value)}
            className="rounded p-1.5 text-inkMuted hover:bg-canvas2 hover:text-ink"
          >
            <MoreHorizontal size={17} />
          </button>
          {showSortMenu && (
            <div role="menu" aria-label="Asset List settings" className="absolute right-0 top-full z-50 mt-1 min-w-[190px] rounded-lg border border-border bg-canvas py-1 shadow-lg">
              <div className="px-3 pb-1 pt-1.5 font-mono text-[9px] uppercase tracking-wider text-inkMuted">Columns</div>
              {allowedAssetListColumns.map(columnKey => {
                const checked = (activeAssetListConfiguration.columns || allowedAssetListColumns).includes(columnKey)
                const required = columnKey === 'symbol'
                return (
                  <label key={columnKey} className={`flex items-center gap-2 px-3 py-1.5 font-mono text-[10px] ${required ? 'cursor-default text-inkMuted' : 'cursor-pointer text-ink hover:bg-canvas2/60'}`}>
                    <input type="checkbox" checked={checked} disabled={required} onChange={() => handleAssetListColumnToggle(columnKey)} className="accent-ink" />
                    <span className="flex-1">{ASSET_LIST_COLUMN_LABELS[columnKey]}</span>
                    {required && <span className="text-[8px] uppercase tracking-wide">Required</span>}
                  </label>
                )
              })}
              <div className="my-1 border-t border-border" />
              <div className="px-3 pb-1 pt-1 font-mono text-[9px] uppercase tracking-wider text-inkMuted">Sort</div>
              <button type="button" role="menuitemradio"
                aria-checked={listOrder.value?.mode === 'manual' || (!listOrder.value && activeAssetListConfiguration.sort?.field === 'manual')}
                disabled={!listOrder.ready || listOrder.saving}
                onClick={() => { handleAssetListSortChange({ field: 'manual', direction: 'asc' }); setShowSortMenu(false) }}
                title="Clear column sorting and restore your saved list order"
                className="w-full px-3 py-1.5 text-left font-mono text-[10px] text-ink hover:bg-canvas2/50 disabled:opacity-40">
                Default order
              </button>
              {sortOptions.filter(opt => (
                allowedAssetListColumns.includes('pnl') || !['pnl_desc', 'pnl_asc'].includes(opt.key)
              )).map(opt => (
                <button key={opt.key} type="button" onClick={() => { setSortMode(opt.key); setShowSortMenu(false) }} className={`w-full px-3 py-1.5 text-left font-mono text-[10px] ${sortMode === opt.key ? 'bg-canvas2 text-ink' : 'text-inkMuted hover:bg-canvas2/50 hover:text-ink'}`}>
                  {opt.label}
                </button>
              ))}
              {activeManualList && <>
                <div className="my-1 border-t border-border" />
                <button type="button" role="menuitem" onClick={() => { setHistoryList(activeManualList); setShowSortMenu(false) }} className="w-full px-3 py-1.5 text-left font-mono text-[10px] text-inkMuted hover:bg-canvas2/50 hover:text-ink">Import history</button>
              </>}
            </div>
          )}
        </div>

        {showListMenu && (
          <div role="menu" aria-label="Asset Lists" className="absolute left-0 top-full z-50 mt-1 w-[240px] rounded-lg border border-border bg-canvas py-1 text-ink shadow-xl">
            {[
              ['Make a copy', Copy],
              ['Rename', Pencil],
              ['Clear list', Eraser],
            ].map(([label, Icon]) => (
              <button
                key={label}
                type="button"
                role="menuitem"
                disabled={!activeManualList || label !== 'Rename'}
                onClick={() => {
                  if (label === 'Rename' && activeManualList) {
                    setWatchlistEditorMode('rename')
                    setWatchlistNameDraft(activeManualList.name)
                    setAssetListPersistenceError(null)
                  }
                  setShowListMenu(false)
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left font-sans text-xs hover:bg-canvas2 disabled:cursor-not-allowed disabled:opacity-35"
              >
                <Icon size={14} />
                {label}
              </button>
            ))}
            <>
              <label className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-xs hover:bg-canvas2">
                <input type="checkbox" role="menuitemcheckbox" aria-label="Group by sector — category"
                  checked={watchlistClassifications.enabled} aria-checked={watchlistClassifications.enabled}
                  disabled={assetListSettingsOwner !== user?.id || configurationSaving}
                  onChange={() => {
                    void persistAssetListConfiguration({ ...activeAssetListConfiguration, groupBySector: !watchlistClassifications.enabled })
                    setShowListMenu(false)
                  }}
                  className="h-3.5 w-3.5 accent-ink disabled:opacity-40" />
                Group by sector — category
              </label>
            </>
            {activeManualList && (
              <button type="button" role="menuitem" onClick={() => { setWatchlistEditorMode('delete'); setShowListMenu(false) }} className="flex w-full items-center gap-2 px-3 py-2 text-left font-sans text-xs text-ember hover:bg-ember/10">
                <Trash2 size={14} /> Delete list
              </button>
            )}
            <div className="my-1 border-t border-border" />
            <button type="button" role="menuitem" onClick={() => { setWatchlistEditorMode('create'); setWatchlistNameDraft(''); setAssetListPersistenceError(null); setShowListMenu(false) }} className="flex w-full items-center gap-2 px-3 py-2 text-left font-sans text-xs hover:bg-canvas2">
              <ListPlus size={14} /> Create new list
            </button>
            <div className="my-1 border-t border-border" />
            <div className="px-3 py-1 font-mono text-[9px] uppercase tracking-wider text-inkMuted">Asset lists</div>
            {positionStageTabs.map(stage => (
              <button key={stage} type="button" role="menuitem" onClick={event => { selectLifecycleList(stage); if (event.detail) event.currentTarget.blur() }} className={`outline-none focus-visible:ring-1 focus-visible:ring-ink/30 flex w-full items-center justify-between px-3 py-1.5 text-left font-sans text-xs hover:bg-canvas2 ${!activeManualList && activeStageTab === stage ? 'bg-canvas2 font-semibold' : ''}`}>
                <span>{POSITION_STAGE_LABELS[stage]}</span><span className="font-mono text-[9px] text-inkMuted">{tabCounts[stage] || 0}</span>
              </button>
            ))}
            {watchlistIconOrder.lists.map(list => (
              <button key={list.listId} type="button" role="menuitem" aria-label={list.name} onClick={() => selectManualList(list.listId)} className={`flex w-full items-center justify-between px-3 py-1.5 text-left font-sans text-xs hover:bg-canvas2 ${activeManualList?.listId === list.listId ? 'bg-canvas2 font-semibold' : ''}`}>
                <span className="truncate">{list.name}</span><span className="ml-2 font-mono text-[9px] text-inkMuted">{list.name.trim().charAt(0).toUpperCase()}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {(watchlistEditorMode === 'create' || watchlistEditorMode === 'rename') && (
        <div className="mb-2 flex items-center gap-1 rounded border border-border bg-canvas2/50 p-1">
          <input autoFocus aria-label={watchlistEditorMode === 'create' ? 'New watchlist name' : 'Rename watchlist'} value={watchlistNameDraft} onChange={event => { setWatchlistNameDraft(event.target.value); setAssetListPersistenceError(null) }} onKeyDown={event => { if (event.key === 'Enter') void handleSaveWatchlistName(); if (event.key === 'Escape') closeWatchlistEditor() }} maxLength={48} className="min-w-0 flex-1 bg-transparent px-1 font-mono text-sm text-ink outline-none" />
          <button type="button" aria-label="Save watchlist name" disabled={!watchlistNameDraft.trim() || watchlistMutationPending} onClick={() => void handleSaveWatchlistName()} className="rounded p-1 text-inkMuted hover:text-ink disabled:opacity-40">{watchlistMutationPending ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}</button>
          <button type="button" aria-label="Cancel watchlist edit" onClick={closeWatchlistEditor} className="rounded p-1 text-inkMuted hover:text-ink"><X size={13} /></button>
        </div>
      )}
      {watchlistEditorMode === 'delete' && activeManualList && (
        <div className="mb-2 flex items-center gap-1 rounded border border-ember/30 bg-ember/10 p-1.5">
          <span className="min-w-0 flex-1 truncate font-mono text-[10px] text-ink">Delete {activeManualList.name}?</span>
          <button type="button" disabled={watchlistMutationPending} onClick={() => void handleDeleteWatchlist()} className="rounded px-1.5 py-1 font-mono text-[9px] uppercase text-ember">Confirm</button>
          <button type="button" aria-label="Cancel delete watchlist" onClick={() => setWatchlistEditorMode(null)} className="rounded p-1 text-inkMuted"><X size={13} /></button>
        </div>
      )}

      <div className="mb-2 flex max-w-full gap-1.5 overflow-x-auto border-b border-border pb-2 pl-[7px]" aria-label="Asset List shortcuts">
        {positionStageTabs.map(stage => {
          const labels = { all: 'A', active: '', working: '', idea: '', closed: '' }
          const colors = { all: 'bg-inkMuted', active: 'bg-forest', working: 'bg-amber-500', idea: 'bg-blue-600', closed: 'bg-ember' }
          const selected = !activeManualList && activeStageTab === stage
          return (
            <button key={stage} type="button" aria-label={POSITION_STAGE_LABELS[stage]} title={POSITION_STAGE_LABELS[stage]} onClick={event => { selectLifecycleList(stage); if (event.detail) event.currentTarget.blur() }} className={`outline-none focus-visible:ring-1 focus-visible:ring-ink/30 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-ink/20 text-[10px] font-semibold transition-colors ${selected ? 'border-ink/40 bg-ink text-canvas' : 'bg-canvas text-inkMuted hover:bg-canvas2'}`}>
              {stage === 'all' ? labels[stage] : <span className={`h-4 w-2.5 ${colors[stage]}`} style={{ clipPath: 'polygon(0 0, 100% 0, 64% 50%, 100% 100%, 0 100%)' }} />}
            </button>
          )
        })}
        <WatchlistIconStrip key={user?.id || 'signed-out'} lists={watchlistIconOrder.lists}
          selectedId={activeManualList?.listId} onSelect={selectManualList}
          onReorder={watchlistIconOrder.save}
          disabled={!watchlistIconOrder.ready || watchlistIconOrder.saving || watchlistMutationPending} />
      </div>

      {showSymbolSearch && createPortal((optionChainUnderlying ? (
        <FloatingOptionChainWindow ariaLabel={`${optionChainUnderlying.symbol} option chain`}>
          <div className="absolute right-3 top-2 z-10">
            <button type="button" aria-label="Close symbol search" onClick={() => setShowSymbolSearch(false)} className="rounded p-1 text-inkMuted hover:bg-canvas2 hover:text-ink"><X size={18} /></button>
          </div>
          <OptionChainPicker
            underlying={optionChainUnderlying}
            watchlists={durableAssetLists}
            activeOptionIdentityKeys={activeOptionIdentityKeys}
            onBack={() => { setOptionChainUnderlying(null); setOpenError(null) }}
            onOpenContract={contract => void handleOpenOptionContract(contract)}
            onAddContract={handleAddOptionContract}
            onInspectWatchlists={handleInspectOptionContractWatchlists}
          />
          {openError && <div role="alert" className="absolute inset-x-0 bottom-0 border-t border-border bg-canvas px-4 py-2 font-mono text-[10px] text-ember">{openError}</div>}
        </FloatingOptionChainWindow>
      ) : (
        <div data-testid="asset-list-symbol-search-overlay" className="fixed inset-0 z-[70] flex items-start justify-center bg-ink/30 px-4 pt-[10vh] backdrop-blur-sm dark:bg-black/55" onMouseDown={event => { if (event.target === event.currentTarget) setShowSymbolSearch(false) }}>
          <div role="dialog" aria-modal="true" aria-label={symbolSearchMode === 'add' ? (activeManualList ? `Add symbol to ${activeManualList.name}` : 'Add symbol') : 'Symbol search'} className="relative w-full max-w-xl rounded-lg border border-border bg-canvas text-ink shadow-2xl">
            {!optionChainUnderlying && <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <div className="font-sans text-base font-semibold">{symbolSearchMode === 'add' ? (activeManualList ? `Add symbol to ${activeManualList.name}` : 'Add symbol') : 'Symbol search'}</div>
              <button type="button" aria-label="Close symbol search" onClick={() => setShowSymbolSearch(false)} className="rounded p-1 text-inkMuted hover:bg-canvas2 hover:text-ink"><X size={18} /></button>
            </div>}
            <form onSubmit={handleOpenSymbol} className="px-4 pb-4 pt-3">
              <div className="flex items-center gap-2 rounded-md border border-border bg-canvas2 px-3 py-2 focus-within:border-ink/40">
                <Search size={16} className="text-inkMuted" />
                <input
                  ref={focusSymbolSearchInput}
                  type="text"
                  value={openSymbol}
                  onChange={event => { setOpenSymbol(event.target.value.toUpperCase()); setOpenError(null) }}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') setShowSymbolSearch(false)
                    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                      event.preventDefault()
                      const count = visibleInstrumentResults.length
                      if (count > 0) {
                        setSelectedInstrumentIndex(current => (
                          (current + (event.key === 'ArrowDown' ? 1 : -1) + count) % count
                        ))
                      }
                    }
                  }}
                  placeholder={symbolSearchMode === 'add' ? (activeManualList ? `Add to ${activeManualList.name}…` : 'Add symbol…') : 'Search symbols…'}
                  maxLength={80}
                  disabled={opening || watchlistMutationPending}
                  aria-label="Search IBKR instruments"
                  className="min-w-0 flex-1 bg-transparent font-mono text-sm uppercase text-ink outline-none placeholder:text-inkMuted/60"
                />
              </div>
              <div className="mt-3 flex flex-wrap gap-2" aria-label="Instrument types">
                {INSTRUMENT_SEARCH_FILTERS.map(filter => {
                  const selected = instrumentSearchFilter === filter.id
                  return (
                    <button
                      key={filter.id}
                      type="button"
                      aria-label={filter.ariaLabel}
                      aria-pressed={selected}
                      onClick={() => {
                        setInstrumentSearchFilter(filter.id)
                        setSelectedInstrumentIndex(0)
                      }}
                      className={`rounded-full px-3 py-1 font-sans text-xs transition-colors ${selected ? 'bg-ink text-canvas' : 'bg-canvas2 text-inkMuted hover:text-ink'}`}
                    >
                      {filter.label}
                    </button>
                  )
                })}
              </div>
              {visibleInstrumentResults.length > 0 && (
                <div className="mt-3 max-h-[56vh] overflow-y-auto border-t border-border" aria-label="IBKR instrument results">
                  {visibleInstrumentResults.map((candidate, index) => {
                    const duplicate = Boolean(activeManualList && manualListContainsCandidate(durableAssetListRows, candidate))
                    const addMode = symbolSearchMode === 'add' && activeManualList
                    const label = addMode
                      ? duplicate
                        ? `${candidate.symbol} is already in ${activeManualList.name}`
                        : `Add ${candidate.symbol} to ${activeManualList.name}`
                      : `Open ${candidate.symbol} workspace`
                    return (
                      <div
                        key={candidate.conid}
                        onMouseMove={() => setSelectedInstrumentIndex(index)}
                        className={`flex w-full items-stretch border-b border-border ${index === selectedInstrumentIndex ? 'bg-canvas2' : 'hover:bg-canvas2/60'}`}
                      >
                        <button
                          type="button"
                          aria-label={label}
                          disabled={opening || watchlistMutationPending || (addMode && duplicate)}
                          onClick={event => void handleOpenSymbol(event, candidate)}
                          className="grid min-w-0 flex-1 grid-cols-[64px_1fr_auto] items-center gap-3 px-3 py-2.5 text-left disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <span className="font-mono text-sm font-semibold text-ink">{candidate.symbol}</span>
                          <span className="min-w-0">
                            <span className="block truncate font-sans text-xs text-ink">{candidate.description || candidate.symbol}</span>
                            <span className="block font-mono text-[9px] text-inkMuted">{duplicate ? `Already in ${activeManualList.name} · ` : ''}{candidate.primaryExchange} · {candidate.currency}</span>
                          </span>
                          {addMode && (opening || watchlistMutationPending ? <Loader2 size={15} className="animate-spin" /> : duplicate ? <Check size={16} /> : <Plus size={16} />)}
                        </button>
                        {!addMode && activeManualList && (
                          <button
                            type="button"
                            aria-label={duplicate ? `${candidate.symbol} is already in ${activeManualList.name}` : `Add ${candidate.symbol} to ${activeManualList.name}`}
                            title={duplicate ? `Already in ${activeManualList.name}` : `Add to ${activeManualList.name}`}
                            disabled={opening || watchlistMutationPending || duplicate}
                            onClick={() => void addCandidateToActiveManualList(candidate)}
                            className="flex w-11 shrink-0 items-center justify-center border-l border-border text-inkMuted hover:bg-canvas hover:text-ink disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {watchlistMutationPending ? <Loader2 size={15} className="animate-spin" /> : duplicate ? <Check size={16} /> : <Plus size={16} />}
                          </button>
                        )}
                        {candidate.derivativeSecTypes?.includes('OPT') && (
                          <button type="button" aria-label={`View ${candidate.symbol} options`} onClick={() => setOptionChainUnderlying(candidate)} className="border-l border-border px-3 font-mono text-[9px] text-inkMuted hover:bg-canvas hover:text-ink">Options</button>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
              {!openSymbol.trim() && <div className="py-8 text-center font-mono text-[10px] text-inkMuted">Search by ticker or company name. Press Enter to use the selected result.</div>}
              {openSymbol.trim().length === 1 && <div className="py-8 text-center font-mono text-[10px] text-inkMuted">Enter at least two letters or numbers.</div>}
              {instrumentSearch.status === 'searching' && <div className="flex items-center justify-center gap-2 py-8 font-mono text-[10px] text-inkMuted"><Loader2 size={13} className="animate-spin" />Searching IBKR…</div>}
              {instrumentSearch.status === 'ready' && visibleInstrumentResults.length === 0 && <div className="py-8 text-center font-mono text-[10px] text-inkMuted">{instrumentSearchFilter === 'options' ? 'No matching option-enabled underlyings.' : 'No matching U.S. stocks or ETFs.'}</div>}
              {instrumentSearch.status === 'unavailable' && <div role="alert" className="mt-3 font-mono text-[10px] text-ember">IBKR instrument search is temporarily unavailable.</div>}
              {instrumentSearch.status === 'error' && <div role="alert" className="mt-3 font-mono text-[10px] text-ember">Instrument search failed. Try again.</div>}
              {openError && <div role="alert" className="mt-2 font-mono text-[10px] text-ember">{openError}</div>}
            </form>
          </div>
        </div>
      )), document.body)}

      <IdeaRemovalToast notice={ideaDeleteNotice} onDismiss={() => setIdeaDeleteNotice(null)} />

      {activeStageTab === 'all' && ideaOrder.error && <div role="alert" className="px-2 text-xs text-ember">{ideaOrder.error}<button onClick={ideaOrder.reload}>Retry Ideas order</button></div>}
      {listOrder.error && (
        <div role="alert" className="mb-2 px-2 py-1.5 text-xs text-ember">{listOrder.error}
          {!listOrder.ready && <button type="button" onClick={listOrder.reload} className="ml-2 underline">Retry loading list order</button>}
        </div>
      )}
      {watchlistIconOrder.error && (
        <div role="alert" className="mb-2 rounded border border-ember/30 px-2 py-1.5 text-xs text-ember">
          {watchlistIconOrder.error}
          {!watchlistIconOrder.ready && <button type="button" onClick={watchlistIconOrder.reload} className="ml-2 underline">Retry loading order</button>}
        </div>
      )}
      {assetListPersistenceError && (
        <div role="alert" className="mb-2 rounded border border-ember/30 bg-ember/10 px-2 py-1.5 font-mono text-[10px] text-ember">
          {assetListPersistenceError}
        </div>
      )}

      <ActionSuccessToast message={assetListNotice?.tone === 'success' ? assetListNotice.text : null} onDismiss={() => setAssetListNotice(null)} testId="asset-list-success" />
      {assetListNotice && assetListNotice.tone !== 'success' && (
        <div
          role={assetListNotice.tone === 'success' ? 'status' : 'alert'}
          className={`mb-2 flex items-center gap-2 rounded border px-2 py-1.5 font-mono text-[10px] ${
            assetListNotice.tone === 'success'
              ? 'border-forest/30 bg-forest/10 text-forest'
              : 'border-ember/30 bg-ember/10 text-ember'
          }`}
        >
          <span className="min-w-0 flex-1">{assetListNotice.text}</span>
          <button type="button" aria-label="Dismiss Asset List notice" onClick={() => setAssetListNotice(null)} className="shrink-0 rounded p-0.5 opacity-70 hover:opacity-100">
            <X size={10} />
          </button>
        </div>
      )}

      {/* Keep both scroll axes inside the table border so long lists retain the same pane padding. */}
      <div className="flex-1 overflow-hidden min-h-0">
        <PositionAssetList
          cards={assetListPositions}
          classifications={watchlistClassifications}
          view={assetListView}
          currentSymbol={highlightedCard?.symbol || currentSymbol}
          configuration={{
            ...activeAssetListConfiguration,
            sort: listOrder.value?.mode === 'manual' ? { field: 'manual', direction: 'asc' }
              : listOrder.value?.mode === 'column' ? listOrder.value.sort : activeAssetListConfiguration.sort || assetListSort,
            lifecycleSections: activeAssetListConfiguration.lifecycleSections || lifecycleSections,
          }}
          ideaOrder={ideaOrder.value}
          manualRowOrder={listOrder.value?.mode === 'manual' ? listOrder.value.rowIds : null}
          reorderScope={`${user?.id || ''}:${activeAssetListViewKey}:${watchlistClassifications.enabled}:${JSON.stringify([listOrder.value,activeStageTab === 'all' ? ideaOrder.value : null])}`}
          onRowReorder={!ideaSelectMode && listOrder.ready && !listOrder.saving && (activeStageTab !== 'all' || (ideaOrder.ready && !ideaOrder.saving))
            ? rowIds => {
              if (activeStageTab === 'all') {
                const ids = new Set(assetListPositions.filter(card=>stageTabFor(card.stage)==='idea').map(card=>String(card.projectIds?.[0] || card.manualMembershipId || card.rowKey || card.symbol)))
                void ideaOrder.save({mode:'manual',rowIds:rowIds.filter(id=>ids.has(id))})
              }
              void listOrder.save({ mode: 'manual', rowIds })
            } : null}
          onOpenCard={handleAssetListCard}
          onVisibleCardsChange={handleVisibleAssetCardsChange}
          onRemoveManualAsset={activeManualList
            ? card => void handleRemoveDurableManualAsset(card)
            : null}
          onRemoveIdea={card => handleDeleteIdeaCards([card])}
          onDeleteIdea={activeStageTab === 'idea' && !ideaSelectMode && !ideaDeletePending
            ? card => handleDeleteIdeaCards([card])
            : null}
          ideaSelectMode={activeStageTab === 'idea' && ideaSelectMode}
          selectedIdeaKeys={selectedIdeaKeys}
          onToggleIdea={toggleIdeaCardSelection}
          onSortChange={listOrder.ready && !listOrder.saving && (activeStageTab !== 'all' || (ideaOrder.ready && !ideaOrder.saving)) ? handleAssetListSortChange : null}
          onGroupToggle={handleLifecycleSectionToggle}
          status={activeManualList && !warmRows && durableAssetListLoading
            ? 'loading'
            : assetListError
              ? 'error'
              : 'ready'}
          error={assetListError ? new Error(assetListError) : null}
        />
      </div>

      {showIdeaControls && ideaSelectMode && (
        <div className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-border bg-canvas2/70 px-2 py-1.5">
          <span className="font-mono text-[10px] text-inkMuted tabular-nums">
            {selectedIdeaCards.length} selected
          </span>
          <button
            type="button"
            aria-label="Delete selected ideas"
            title="Delete selected ideas"
            disabled={ideaDeletePending || selectedIdeaCards.length === 0}
            onClick={() => handleDeleteIdeaCards(selectedIdeaCards)}
            className="inline-flex items-center gap-1 rounded border border-ember/30 bg-ember/10 px-2 py-1 font-mono text-[10px] text-ember transition-colors hover:bg-ember/15 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {ideaDeletePending ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
            Delete
          </button>
        </div>
      )}
    </div>
  )
}
